import React, { useEffect, useState } from "react";
import { api, API_BASE, formatApiErrorDetail, apiHref } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Landmark, Plus, Printer, ExternalLink, CheckCircle2, Mail, Link as LinkIcon, Copy, Calendar as CalIcon, X, Zap, Download, RefreshCcw, AlertTriangle, Pencil, Check, Search, Undo2, ArchiveRestore, Trash2, ChevronRight, ChevronDown, History } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatLocalDate, toPlantISODate, plantPresetRange, localToday, shiftLocalDate } from "@/lib/dates";
import QuickEditDocketPopover from "@/components/QuickEditDocketPopover";
import HistoryImportDialog from "@/components/HistoryImportDialog";

const STATUS_COLOR = { unpaid: "border-primary-orange text-primary-orange", paid: "border-white text-ink", overdue: "border-red-700 text-red-400" };

const fmt = (d) => d ? d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "";
// Convert a Date picker value → YYYY-MM-DD in PLANT time (Australia/Hobart).
// Previously used browser-local time, which showed US dates for viewers off
// site. All date filters now stay locked to plant time.
const toISODate = (d) => toPlantISODate(d);
// Parse a docket's local calendar date (Tasmania time), not UTC. A load poured at
// 06:00 AEDT is stored as 19:00 UTC the previous day; slicing the ISO would put
// it in the wrong invoice period.
const parseDocketDate = (d) => formatLocalDate(d.loaded_at || d.created_at || "");

// v3.6h — Consolidated Export bar with a "Since last export" vs "All time"
// toggle. Historically two lookalike orange buttons (MYOB AR TXT vs MYOB Pro
// TXT) confused Accounts staff who imported the wrong one into MYOB. Now
// the Pro (uBatch-matched Professional Sales) format is the primary CTA;
// AR (Sales-Item) sits behind a "More formats" popover.
function ExportBar() {
  const [open, setOpen] = useState(false);
  const [includeAll, setIncludeAll] = useState(false);
  const [scope, setScope] = useState("all");         // "all" | "today" | "yesterday" | "this_week" | "pick"
  const [pickDate, setPickDate] = useState(() => shiftLocalDate(localToday(), -1));

  const q = (() => {
    const params = new URLSearchParams();
    if (includeAll) params.set("include_exported", "true");
    // Scope filter — all dates computed in PLANT time (Australia/Hobart).
    const today = localToday();
    if (scope === "today") {
      params.set("date", today);
    } else if (scope === "yesterday") {
      params.set("date", shiftLocalDate(today, -1));
    } else if (scope === "this_week") {
      const r = plantPresetRange("this_week");
      params.set("from_date", r.from);
      params.set("to_date", r.to);
    } else if (scope === "pick" && pickDate) {
      params.set("date", pickDate);
    }
    const s = params.toString();
    return s ? `?${s}` : "";
  })();

  const scopeLabel = {
    all: "All Invoices", today: "Today", yesterday: "Yesterday",
    this_week: "This Week", pick: pickDate || "Pick a Day",
  }[scope] || "All";

  const btn = "border-2 rounded-none px-3 py-2 uppercase tracking-widest text-xs font-bold inline-flex items-center gap-1.5 no-underline";
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="myob-export-bar">
      <a
        href={apiHref(`/invoices/export/myob-professional.txt${q}`)}
        target="_blank" rel="noreferrer"
        data-testid="export-myob-pro-txt-btn"
        title="MYOB Professional Sales · 43 columns · no header · matches uBatch. Import via File → Import Data → Sales → Professional Sales"
        className={`${btn} border-primary-orange bg-primary-orange text-black hover:bg-[rgb(var(--primary-hover))]`}
      >
        <Download className="w-4 h-4" />MYOB Pro · TXT
        <span className="mono text-[9px] font-bold ml-1 opacity-70">RECOMMENDED</span>
      </a>

      {/* v3.9 · Day / range scope selector */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            data-testid="export-scope-btn"
            className={`${btn} border-emerald-600 text-emerald-400 hover:bg-emerald-600 hover:text-black`}
            title="Filter which invoices are exported"
          >
            <CalIcon className="w-3.5 h-3.5" />{scopeLabel}
          </button>
        </PopoverTrigger>
        <PopoverContent className="rounded-none bg-surface border border-industrial w-72 p-3" align="end">
          <div className="label-tag mb-2">Export scope</div>
          <div className="grid gap-1">
            {[
              ["all",       "All Invoices",     "Every eligible invoice"],
              ["today",     "Today",            "Invoices issued today"],
              ["yesterday", "Yesterday",        "Invoices issued yesterday"],
              ["this_week", "This Week",        "Monday → today"],
              ["pick",      "Pick a Day",       "Choose any date"],
            ].map(([k, l, hint]) => (
              <button
                key={k}
                onClick={() => setScope(k)}
                data-testid={`export-scope-${k}`}
                className={`p-2 text-left border ${scope === k ? "border-emerald-500 bg-emerald-950/20 text-emerald-300" : "border-industrial text-muted-industrial hover:text-ink"}`}
              >
                <div className="mono text-[10px] uppercase tracking-widest font-bold">{l}</div>
                <div className="mono text-[9px] opacity-70">{hint}</div>
              </button>
            ))}
          </div>
          {scope === "pick" && (
            <div className="mt-2 pt-2 border-t border-industrial">
              <div className="label-tag mb-1">Day</div>
              <Input
                data-testid="export-scope-pick-date"
                type="date"
                value={pickDate}
                onChange={(e) => setPickDate(e.target.value)}
                className="rounded-none bg-transparent border-industrial mono"
              />
            </div>
          )}
        </PopoverContent>
      </Popover>

      <label className="text-[10px] mono uppercase tracking-widest text-muted-industrial flex items-center gap-1.5 cursor-pointer" data-testid="include-all-toggle">
        <Checkbox checked={includeAll} onCheckedChange={setIncludeAll} data-testid="include-all-checkbox" />
        {includeAll ? "Include Already-Exported" : "Since Last Export"}
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid="more-exports-btn"
            className={`${btn} border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange`}
          >
            More formats
          </button>
        </PopoverTrigger>
        <PopoverContent className="rounded-none bg-surface border border-industrial w-80 p-3" align="end">
          <div className="label-tag mb-1">Other Export Formats</div>
          <div className="text-[11px] text-muted-industrial mb-3">
            Use these only if your accountant asks for a specific one.
            <div className="mt-1 flex items-start gap-1.5 p-2 border border-amber-500/40 bg-amber-500/5">
              <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0 mt-0.5" />
              <span className="text-[10px]"><b>AR/Item Sales are NOT the same format as Professional Sales.</b> If you import AR into the Professional wizard, MYOB will reject it.</span>
            </div>
          </div>
          <div className="grid gap-1.5">
            <ExportLink href={apiHref(`/invoices/export/myob-accountright.txt${q}`)} testid="export-myob-ar-txt-btn" label="MYOB AR · TXT" hint="Item Sales · 31 cols · with header" />
            <ExportLink href={apiHref(`/invoices/export/myob-accountright.csv${q}`)} testid="export-myob-ar-btn"     label="MYOB AR · CSV" hint="Item Sales · 31 cols · with header" />
            <ExportLink href={apiHref(`/invoices/export/myob.csv${q}`)}              testid="export-myob-btn"        label="MYOB Business · CSV" hint="Older MYOB Business (v19/AE)" />
            <ExportLink href={apiHref(`/invoices/export/xero.csv${q}`)}              testid="export-xero-btn"        label="Xero · CSV" hint="For Xero-based bookkeepers" />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function ExportLink({ href, label, hint, testid }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
       className="border border-industrial hover:border-primary-orange hover:text-primary-orange px-3 py-2 flex items-center justify-between gap-2 group">
      <div>
        <div className="mono text-xs font-bold uppercase tracking-widest">{label}</div>
        <div className="text-[10px] text-muted-industrial mono">{hint}</div>
      </div>
      <Download className="w-3.5 h-3.5 opacity-40 group-hover:opacity-100" />
    </a>
  );
}

// v3.6k — Compact date filter for the invoice LIST on the Accounts page.
// Presets (Today · Yesterday · This Week · This Month · All) plus a "Pick a
// day" calendar dropdown so accounts can jump to any specific date without
// scrolling through history.
function InvoiceDateFilter({ preset, setPreset, singleDay, setSingleDay, total, filteredTotal }) {
  const [cal, setCal] = useState(false);
  const [monthCursor, setMonthCursor] = useState(singleDay || new Date());
  const dayLabel = singleDay ? singleDay.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "Pick a day";
  const chips = [
    { key: "today",      label: "Today" },
    { key: "yesterday",  label: "Yesterday" },
    { key: "this_week",  label: "This Week" },
    { key: "this_month", label: "This Month" },
    { key: "all",        label: "All" },
  ];
  return (
    <div className="card-industrial p-3 flex items-center gap-2 flex-wrap" data-testid="invoice-date-filter">
      <div className="label-tag flex items-center gap-1.5 mr-1">
        <CalIcon className="w-3.5 h-3.5" />Show
      </div>
      {chips.map((c) => (
        <button
          key={c.key}
          data-testid={`inv-date-${c.key}`}
          onClick={() => setPreset(c.key)}
          className={`mono uppercase tracking-widest text-[10px] px-3 py-1.5 border ${
            preset === c.key
              ? "bg-primary-orange text-black border-primary-orange"
              : "border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange"
          }`}
        >
          {c.label}
        </button>
      ))}
      <Popover open={cal} onOpenChange={setCal}>
        <PopoverTrigger asChild>
          <button
            data-testid="inv-date-pick-day"
            onClick={() => setPreset("day")}
            className={`mono uppercase tracking-widest text-[10px] px-3 py-1.5 border inline-flex items-center gap-1.5 ${
              preset === "day"
                ? "bg-primary-orange text-black border-primary-orange"
                : "border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange"
            }`}
          >
            <CalIcon className="w-3 h-3" />
            {preset === "day" ? dayLabel : "Pick a Day"}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="rounded-none bg-surface border border-industrial p-0 w-auto">
          <Calendar
            mode="single"
            selected={singleDay}
            onSelect={(d) => {
              if (!d) return;
              setSingleDay(d);
              setPreset("day");
              setCal(false);
            }}
            month={monthCursor}
            onMonthChange={setMonthCursor}
            className="p-2"
          />
          <div className="border-t border-industrial p-2 flex items-center justify-between">
            <button
              onClick={() => { const y = new Date(); y.setDate(y.getDate() - 1); setSingleDay(y); setMonthCursor(y); setPreset("day"); setCal(false); }}
              className="text-[10px] mono uppercase tracking-widest text-muted-industrial hover:text-primary-orange"
            >
              ← Yesterday
            </button>
            <button
              onClick={() => { const t = new Date(); setSingleDay(t); setMonthCursor(t); setPreset("day"); setCal(false); }}
              className="text-[10px] mono uppercase tracking-widest text-primary-orange"
            >
              Today
            </button>
            <button
              onClick={() => { const t = new Date(); t.setDate(t.getDate() + 1); setSingleDay(t); setMonthCursor(t); setPreset("day"); setCal(false); }}
              className="text-[10px] mono uppercase tracking-widest text-muted-industrial hover:text-primary-orange"
            >
              Tomorrow →
            </button>
          </div>
        </PopoverContent>
      </Popover>
      <div className="text-[11px] mono text-muted-industrial ml-auto">
        Showing <span className="text-primary-orange font-bold">{filteredTotal}</span> of {total} invoice{total === 1 ? "" : "s"}
      </div>
    </div>
  );
}

export default function Invoices() {
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dockets, setDockets] = useState([]);
  const [open, setOpen] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [selected, setSelected] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailInv, setEmailInv] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [dateOpen, setDateOpen] = useState(false);
  const [datePreset, setDatePreset] = useState("today");   // "all" | "today" | "yesterday" | "this_week" | "last_week" | "this_month" | "last_month" | "custom"
  const [dailyOpen, setDailyOpen] = useState(() => new URLSearchParams(window.location.search).get("daily") === "1");
  const [recallOpen, setRecallOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetInitialDate, setResetInitialDate] = useState("");
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [rebuildOpen, setRebuildOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rebuildDate, setRebuildDate] = useState("");
  // v3.6k — separate filter for the invoice LIST (default = today so accounts
  // isn't buried in a wall of history invoices whenever they open the page).
  const [listPreset, setListPreset] = useState("today");    // today | yesterday | this_week | this_month | all | day
  const [listSingleDay, setListSingleDay] = useState(new Date());
  const [editTarget, setEditTarget] = useState(null);
  // v3.16 — Inline docket peek: which invoice row is expanded + its docket rows.
  const [expandedInv, setExpandedInv] = useState(null);
  const [expandedDockets, setExpandedDockets] = useState([]);
  const [expandedLoading, setExpandedLoading] = useState(false);
  const [mixes, setMixes] = useState([]);
  const [extraTags, setExtraTags] = useState([]);

  // Compute {from,to} for a preset key using PLANT time (Australia/Hobart).
  // Returns Date objects (00:00 plant-day) so the calendar picker stays happy.
  const rangeForPreset = (key) => {
    if (key === "all") return { from: undefined, to: undefined };
    const r = plantPresetRange(key);
    if (!r.from) return null;   // custom → don't touch
    // Turn "YYYY-MM-DD" plant-day into a Date object anchored at that day.
    const toDate = (iso) => {
      const [y, m, d] = iso.split("-").map((x) => parseInt(x, 10));
      return new Date(y, m - 1, d);
    };
    return { from: toDate(r.from), to: toDate(r.to) };
  };

  const applyPreset = (key) => {
    setDatePreset(key);
    if (key === "custom") { setDateOpen(true); return; }
    const r = rangeForPreset(key);
    if (r) setDateRange(r);
  };

  const load = () => Promise.all([api.get("/invoices"), api.get("/customers")]).then(([i, c]) => { setList(i.data); setCustomers(c.data); });
  const pushAccounting = async (inv) => {
    try {
      const { data } = await api.post("/accounting/push", { invoice_ids: [inv.id] });
      const r = data.results?.[0] || {};
      if (r.status === "pushed") toast.success(`${inv.invoice_no} pushed to ${data.provider?.toUpperCase()}${r.external_no ? ` as #${r.external_no}` : ""}`);
      else if (r.status === "not_connected") toast.warning(r.error || "Accounting provider not connected — open Settings → Accounting Hub");
      else toast.error(r.error || `Push ${r.status}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  // v3.16 — Load mixes + extra tags once for the inline quick-edit popover.
  useEffect(() => {
    Promise.all([api.get("/mixes"), api.get("/extra-tags")])
      .then(([m, t]) => { setMixes(m.data || []); setExtraTags(t.data || []); })
      .catch(() => {});
  }, []);

  // Expand / collapse a single invoice row to reveal its docket lines.
  const toggleExpand = async (inv) => {
    if (expandedInv === inv.id) {
      setExpandedInv(null);
      setExpandedDockets([]);
      return;
    }
    setExpandedInv(inv.id);
    setExpandedDockets([]);
    setExpandedLoading(true);
    try {
      const r = await api.get(`/dockets?invoice_id=${inv.id}&limit=500`);
      setExpandedDockets(r.data || []);
    } catch (e) {
      toast.error("Failed to load dockets for this invoice");
    } finally {
      setExpandedLoading(false);
    }
  };

  const reloadExpanded = async () => {
    if (!expandedInv) return;
    try {
      const r = await api.get(`/dockets?invoice_id=${expandedInv}&limit=500`);
      setExpandedDockets(r.data || []);
      // Refresh the parent invoice list too so totals & subtotal update.
      load();
    } catch (e) { /* ignore */ }
  };

  // v3.16 — All invoice date filtering uses PLANT time (Australia/Hobart).
  // Previously derived from browser-local Date arithmetic, which drifted
  // by a whole calendar day for viewers outside AEST/AEDT (e.g. US TZs)
  // and hid morning-shift invoices when the UTC date lagged.
  const filteredInvoices = (() => {
    if (listPreset === "all") return list;
    const inRange = (isoStr, from, to) => {
      const d = formatLocalDate(isoStr);   // invoice day in PLANT TZ
      if (!d) return false;
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    };
    const today = localToday();
    if (listPreset === "day") {
      const d = toPlantISODate(listSingleDay) || today;
      return list.filter((i) => inRange(i.issued_at, d, d));
    }
    if (listPreset === "today") {
      return list.filter((i) => inRange(i.issued_at, today, today));
    }
    if (listPreset === "yesterday") {
      const d = shiftLocalDate(today, -1);
      return list.filter((i) => inRange(i.issued_at, d, d));
    }
    if (listPreset === "this_week") {
      const r = plantPresetRange("this_week");
      return list.filter((i) => inRange(i.issued_at, r.from, r.to));
    }
    if (listPreset === "this_month") {
      const r = plantPresetRange("this_month");
      return list.filter((i) => inRange(i.issued_at, r.from, r.to));
    }
    return list;
  })();

  const openNew = () => {
    setCustomerId("");
    setSelected([]);
    setDockets([]);
    setDatePreset("today");
    setDateRange(rangeForPreset("today"));
    setOpen(true);
  };
  const onCustomer = async (id) => {
    setCustomerId(id);
    setSelected([]);
    const r = await api.get(`/dockets?customer_id=${id}&status=delivered`);
    setDockets(r.data.filter((d) => !d.invoice_id));
  };
  const create = async () => {
    if (selected.length === 0) return toast.error("Select at least one docket");
    try {
      const r = await api.post("/invoices", { customer_id: customerId, docket_ids: selected, tax_rate: 0.10 });
      toast.success(`Invoice ${r.data.invoice_no} created`);
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  // Marking an invoice paid says money landed in the bank, so it now asks first.
  // It used to fire on a single click in a row of six tiny buttons next to Edit and
  // Delete, which is how invoices ended up "received" when nothing had been received.
  const pay = async (inv) => {
    const ok = window.confirm(
      `Record payment RECEIVED for ${inv.invoice_no}?\n\n` +
      `${inv.customer_name}\n$${inv.total.toFixed(2)}\n\n` +
      `Only do this if the money is actually in the bank. Click Cancel if you were after the Pay Link.`
    );
    if (!ok) return;
    try {
      await api.post(`/invoices/${inv.id}/pay`);
      toast.success(`${inv.invoice_no} marked paid`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to mark paid"); }
  };

  // Undo a Mark Paid that was never received. Stripe payments are blocked server-side.
  const unpay = async (inv) => {
    const ok = window.confirm(
      `Reverse the payment mark on ${inv.invoice_no}?\n\n` +
      `${inv.customer_name}\n$${inv.total.toFixed(2)}\n\n` +
      `This puts it back to UNPAID and is logged. Use this when it was marked paid by mistake.`
    );
    if (!ok) return;
    try {
      await api.post(`/invoices/${inv.id}/unpay`);
      toast.success(`${inv.invoice_no} back to unpaid`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't reverse it"); }
  };

  // v3.11 · Void (delete) a single invoice from the list. Voiding keeps
  // the audit trail (status="voided") and un-links its dockets so they
  // can be re-invoiced later. Paid invoices are blocked server-side —
  // caller must refund first.
  const voidInvoice = async (inv) => {
    if (inv.status === "voided") { toast.info("Already voided"); return; }
    if (inv.status === "paid") {
      toast.error(`${inv.invoice_no} is PAID — refund the payment first, then void`);
      return;
    }
    const reason = window.prompt(
      `Delete invoice ${inv.invoice_no} · $${inv.total.toFixed(2)} · ${inv.customer_name}?\n\n` +
      `Enter a short reason (kept in the audit log). Click Cancel to bail out.`,
      "created in error"
    );
    if (reason === null) return;
    try {
      await api.post(`/invoices/${inv.id}/void`, { reason: reason || "no reason given" });
      toast.success(`${inv.invoice_no} voided`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Void failed");
    }
  };

  const copyPayLink = async (inv) => {
    try {
      const r = await api.post(`/invoices/${inv.id}/payment-link`);
      const url = `${window.location.origin}/pay/${r.data.token}`;
      await navigator.clipboard.writeText(url);
      toast.success(`Payment link copied to clipboard`);
    } catch (e) {
      toast.error("Couldn't create payment link");
    }
  };

  const openEmail = (inv) => {
    const customer = customers.find((c) => c.id === inv.customer_id);
    setEmailInv(inv);
    setEmailTo(customer?.email || "");
    setEmailOpen(true);
  };
  const sendEmail = async () => {
    if (!emailTo) return toast.error("Recipient email required");
    setEmailSending(true);
    try {
      const r = await api.post(`/invoices/${emailInv.id}/email`, { recipient_email: emailTo });
      if (r.data.sandbox && r.data.recipient !== r.data.intended_recipient) {
        toast.success(`Sandbox: invoice ${emailInv.invoice_no} routed to ${r.data.recipient} (would have gone to ${r.data.intended_recipient})`);
      } else {
        toast.success(`Invoice ${emailInv.invoice_no} sent to ${r.data.recipient}`);
      }
      setEmailOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="invoices-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div><div className="label-tag">BILLING</div><h1 className="heading text-3xl font-black uppercase">Invoices</h1></div>
        <div className="flex gap-2 flex-wrap items-center">
          <Button data-testid="recall-day-btn" onClick={() => setRecallOpen(true)} className="rounded-none bg-amber-500/15 border-2 border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-black uppercase tracking-widest text-xs font-bold"><Search className="w-4 h-4 mr-1" />Recall Day</Button>
          <Button data-testid="rebuild-day-btn" onClick={() => { setRebuildDate(""); setRebuildOpen(true); }} className="rounded-none bg-emerald-600/15 border-2 border-emerald-600 text-emerald-400 hover:bg-emerald-600 hover:text-black uppercase tracking-widest text-xs font-bold" title="Reset a day's invoices AND immediately re-issue them from the current dockets"><RefreshCcw className="w-4 h-4 mr-1" />Reset &amp; Rebuild</Button>
          <Button data-testid="reset-day-btn" onClick={() => setResetOpen(true)} className="rounded-none bg-red-600/15 border-2 border-red-600 text-red-400 hover:bg-red-600 hover:text-white uppercase tracking-widest text-xs font-bold"><Undo2 className="w-4 h-4 mr-1" />Reset Day</Button>
          {isAdmin && <Button data-testid="purge-trial-btn" onClick={() => setPurgeOpen(true)} className="rounded-none bg-fuchsia-600/15 border-2 border-fuchsia-600 text-fuchsia-400 hover:bg-fuchsia-600 hover:text-white uppercase tracking-widest text-xs font-bold" title="Archive a range of trial data (dockets, invoices, jobs, etc)"><Trash2 className="w-4 h-4 mr-1" />Archive Range</Button>}
          <Button data-testid="daily-run-btn" onClick={() => setDailyOpen(true)} className="rounded-none bg-primary-orange/20 border-2 border-primary-orange text-primary-orange hover:bg-primary-orange hover:text-black uppercase tracking-widest text-xs font-bold"><Zap className="w-4 h-4 mr-1" />Daily Invoice Run</Button>
          <ExportBar />
          <Button data-testid="import-history-btn" variant="outline" onClick={() => setHistoryOpen(true)} className="rounded-none border-industrial uppercase tracking-widest text-xs font-bold" title="Import past delivery dockets from your old system"><History className="w-4 h-4 mr-1 text-primary-orange" />Import History</Button>
          <Button data-testid="new-invoice-btn" onClick={openNew} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold"><Plus className="w-4 h-4 mr-1" />New Invoice</Button>
        </div>
      </div>

      <HistoryImportDialog open={historyOpen} onOpenChange={setHistoryOpen} onDone={load} />
      <DailyRunDialog open={dailyOpen} onOpenChange={setDailyOpen} onDone={load} />
      <RecallDayDialog open={recallOpen} onOpenChange={setRecallOpen} onDone={load} openReset={(d) => { setRecallOpen(false); setResetOpen(true); setResetInitialDate(d); }} openRebuild={(d) => { setRecallOpen(false); setRebuildDate(d); setRebuildOpen(true); }} />
      <ResetDayDialog open={resetOpen} onOpenChange={setResetOpen} initialDate={resetInitialDate} onDone={load} openDailyRun={() => { setResetOpen(false); setDailyOpen(true); }} />
      <ResetAndRebuildDialog open={rebuildOpen} onOpenChange={setRebuildOpen} initialDate={rebuildDate} onDone={load} />
      {isAdmin && <PurgeTrialDialog open={purgeOpen} onOpenChange={setPurgeOpen} onDone={load} />}

      <InvoiceDateFilter
        preset={listPreset}
        setPreset={setListPreset}
        singleDay={listSingleDay}
        setSingleDay={setListSingleDay}
        total={list.length}
        filteredTotal={filteredInvoices.length}
      />

      <div className="card-industrial overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-industrial"><tr className="text-left">
            <th className="p-3 label-tag">Invoice</th><th className="p-3 label-tag">Customer</th>
            <th className="p-3 label-tag text-right">Subtotal</th><th className="p-3 label-tag text-right">Tax</th>
            <th className="p-3 label-tag text-right">Total</th><th className="p-3 label-tag">Status</th>
            <th className="p-3 label-tag">Due</th><th className="p-3 label-tag text-right">Actions</th>
          </tr></thead>
          <tbody>
            {filteredInvoices.map((i) => (
              <React.Fragment key={i.id}>
              <tr data-testid={`inv-row-${i.invoice_no}`} className="border-b border-industrial hover:bg-surface-2">
                <td className="p-3 mono">
                  <button
                    data-testid={`expand-inv-${i.invoice_no}`}
                    onClick={() => toggleExpand(i)}
                    title="Show / hide dockets on this invoice"
                    className="mr-1 text-muted-industrial hover:text-primary-orange align-middle"
                  >
                    {expandedInv === i.id ? <ChevronDown className="w-3.5 h-3.5 inline" /> : <ChevronRight className="w-3.5 h-3.5 inline" />}
                  </button>
                  {i.invoice_no}
                </td>
                <td className="p-3">{i.customer_name}</td>
                <td className="p-3 mono text-right">${i.subtotal.toFixed(2)}</td>
                <td className="p-3 mono text-right">${i.tax.toFixed(2)}</td>
                <td className="p-3 mono text-right font-bold text-primary-orange">${i.total.toFixed(2)}</td>
                <td className="p-3"><span className={`mono text-[10px] uppercase px-2 py-0.5 border ${STATUS_COLOR[i.status]}`}>{i.status}</span>
                  {["rate", "window"].includes(i.lines_rebuild_mode) && (
                    <span data-testid={`rebuilt-badge-${i.invoice_no}`} title={`Line rates on this invoice were RECONSTRUCTED (${i.lines_rebuild_mode} match) when legacy data was restored — every concrete line was given one blended $/m³ to reconcile to the header total. Check these against the original before sending.`}
                      className="ml-1 mono text-[10px] uppercase px-1.5 py-0.5 border border-amber-500 text-amber-400">rates rebuilt</span>
                  )}
                </td>
                <td className="p-3 mono text-xs text-muted-industrial">{i.due_at?.slice(0, 10)}</td>
                <td className="p-3 text-right space-x-2">
                  <a href={apiHref(`/invoices/${i.id}/pdf`)} target="_blank" rel="noreferrer" data-testid={`pdf-inv-${i.invoice_no}`} className="text-primary-orange hover:underline inline-flex items-center gap-1 text-xs uppercase"><Printer className="w-3.5 h-3.5" /> PDF <ExternalLink className="w-3 h-3" /></a>
                  {i.status !== "voided" && (
                    <button data-testid={`push-acct-inv-${i.invoice_no}`} onClick={() => pushAccounting(i)}
                      className={`text-xs uppercase inline-flex items-center gap-1 ${i.accounting_sync?.status === "pushed" ? "text-emerald-500" : i.accounting_sync?.status === "failed" || i.accounting_sync?.status === "not_connected" ? "text-amber-400" : "text-ink hover:text-primary-orange"}`}
                      title={i.accounting_sync ? `${i.accounting_sync.provider?.toUpperCase()} · ${i.accounting_sync.status}${i.accounting_sync.error ? " · " + i.accounting_sync.error : ""}${i.accounting_sync.external_no ? " · #" + i.accounting_sync.external_no : ""}` : "Push this invoice to Xero / QuickBooks / MYOB"}>
                      <Landmark className="w-3.5 h-3.5" /> {i.accounting_sync?.status === "pushed" ? "Synced" : "Push"}
                    </button>
                  )}
                  <button data-testid={`email-inv-${i.invoice_no}`} onClick={() => openEmail(i)} className="text-xs uppercase text-ink hover:text-primary-orange inline-flex items-center gap-1" title={i.last_emailed_at ? `Last sent ${i.last_emailed_at.slice(0,10)} to ${i.last_emailed_to}` : "Send by email"}><Mail className="w-3.5 h-3.5" /> {i.last_emailed_at ? "Resend" : "Email"}</button>
                  {i.status !== "paid" && <button data-testid={`paylink-inv-${i.invoice_no}`} onClick={() => copyPayLink(i)} className="text-xs uppercase text-ink hover:text-primary-orange inline-flex items-center gap-1" title="Copy customer payment link (Stripe)"><LinkIcon className="w-3.5 h-3.5" /> Pay Link</button>}
                  {i.status === "unpaid" && <button data-testid={`edit-inv-${i.invoice_no}`} onClick={() => setEditTarget(i)} className="text-xs uppercase text-ink hover:text-primary-orange inline-flex items-center gap-1" title="Edit line items and totals (audit trail kept)"><Pencil className="w-3.5 h-3.5" /> Edit</button>}
                  {i.status !== "paid" && <button data-testid={`pay-inv-${i.invoice_no}`} onClick={() => pay(i)} className="text-xs uppercase text-emerald-500 hover:text-emerald-400 inline-flex items-center gap-1" title="Record that this money has been RECEIVED in the bank"><CheckCircle2 className="w-3.5 h-3.5" /> Mark Paid</button>}
                  {i.status === "paid" && i.paid_via !== "stripe" && <button data-testid={`unpay-inv-${i.invoice_no}`} onClick={() => unpay(i)} className="text-xs uppercase text-amber-400 hover:text-amber-300 inline-flex items-center gap-1" title="Marked paid by mistake? Put it back to unpaid"><Undo2 className="w-3.5 h-3.5" /> Not Paid</button>}
                  {i.status !== "voided" && <button data-testid={`void-inv-${i.invoice_no}`} onClick={() => voidInvoice(i)} className="text-xs uppercase text-red-400 hover:text-red-300 inline-flex items-center gap-1" title="Void this invoice (unlinks its dockets so they can be re-invoiced)"><Trash2 className="w-3.5 h-3.5" /> Delete</button>}
                </td>
              </tr>
              {expandedInv === i.id && (
                <tr key={`${i.id}-dockets`} data-testid={`inv-dockets-${i.invoice_no}`} className="border-b border-industrial bg-surface-2/40">
                  <td colSpan="8" className="p-3">
                    {expandedLoading && <div className="text-xs mono text-muted-industrial">Loading dockets…</div>}
                    {!expandedLoading && expandedDockets.length === 0 && (
                      <div className="text-xs mono text-muted-industrial">No dockets attached to this invoice.</div>
                    )}
                    {!expandedLoading && expandedDockets.length > 0 && (
                      <div className="space-y-1">
                        <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial pb-1">
                          {expandedDockets.length} docket{expandedDockets.length === 1 ? "" : "s"} on this invoice
                          {i.status !== "unpaid" && <span className="ml-2 text-amber-400"> · edits to a {i.status} invoice won't retroactively change the printed line snapshot</span>}
                        </div>
                        {expandedDockets.map((d) => (
                          <div
                            key={d.id}
                            data-testid={`inv-docket-row-${d.docket_no}`}
                            className="flex items-center gap-2 text-[11px] mono border border-industrial px-2 py-1.5"
                          >
                            <span className="font-bold">{d.docket_no}</span>
                            <span className="text-muted-industrial">{d.mix_code}</span>
                            <span className="text-muted-industrial">·</span>
                            <span>{Number(d.quantity_m3 || 0).toFixed(1)}m³</span>
                            <span className="text-muted-industrial">@</span>
                            <span>${Number(d.price_per_m3 || 0).toFixed(2)}/m³</span>
                            <span className="ml-auto text-primary-orange font-bold">
                              ${Number(d.invoice_total ?? d.total ?? 0).toFixed(2)}
                            </span>
                            <QuickEditDocketPopover
                              docket={d}
                              mixes={mixes}
                              extraTags={extraTags}
                              testIdSuffix={d.docket_no}
                              onSaved={reloadExpanded}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
            {filteredInvoices.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-muted-industrial">
              {list.length === 0 ? "No invoices yet." : "No invoices match this date filter."}
            </td></tr>}
          </tbody>
        </table>
      </div>

      {/* v3.11 — Edit invoice dialog */}
      {editTarget && (
        <EditInvoiceDialog
          invoice={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={async (updated, shouldResend) => {
            setEditTarget(null);
            await load();
            if (shouldResend) {
              try {
                await api.post(`/invoices/${updated.id}/email`);
                toast.success("Updated invoice re-emailed to customer");
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Edit saved but email failed");
              }
            }
          }}
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-2xl" data-testid="invoice-dialog">
          <DialogHeader><DialogTitle className="heading uppercase">New Invoice</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="label-tag mb-1">Customer</div>
                <Select value={customerId} onValueChange={onCustomer}>
                  <SelectTrigger data-testid="inv-customer-select" className="rounded-none bg-transparent border-industrial"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent className="rounded-none bg-surface border-industrial">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <div className="label-tag mb-1 flex items-center gap-1.5">
                  <CalIcon className="w-3 h-3" /> Delivery date
                </div>
                <Select value={datePreset} onValueChange={applyPreset}>
                  <SelectTrigger data-testid="inv-date-preset-select" className="rounded-none bg-transparent border-industrial">
                    <SelectValue placeholder="Filter dockets by day" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-surface border-industrial">
                    <SelectItem value="today" data-testid="inv-date-preset-today">Today</SelectItem>
                    <SelectItem value="yesterday" data-testid="inv-date-preset-yesterday">Yesterday</SelectItem>
                    <SelectItem value="this_week" data-testid="inv-date-preset-this-week">This week</SelectItem>
                    <SelectItem value="last_week" data-testid="inv-date-preset-last-week">Last week</SelectItem>
                    <SelectItem value="this_month" data-testid="inv-date-preset-this-month">This month</SelectItem>
                    <SelectItem value="last_month" data-testid="inv-date-preset-last-month">Last month</SelectItem>
                    <SelectItem value="all" data-testid="inv-date-preset-all">All dates</SelectItem>
                    <SelectItem value="custom" data-testid="inv-date-preset-custom">Custom range…</SelectItem>
                  </SelectContent>
                </Select>
                {datePreset === "custom" && (
                  <Popover open={dateOpen} onOpenChange={setDateOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="inv-date-range-btn"
                        className="mt-1.5 w-full mono text-[11px] uppercase tracking-widest px-2 py-1.5 border border-industrial hover:border-primary-orange hover:text-primary-orange inline-flex items-center justify-center gap-1.5"
                      >
                        <CalIcon className="w-3 h-3" />
                        {dateRange.from
                          ? (dateRange.to && toISODate(dateRange.to) !== toISODate(dateRange.from)
                              ? `${fmt(dateRange.from)} → ${fmt(dateRange.to)}`
                              : fmt(dateRange.from))
                          : "Pick range…"}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-auto p-0" data-testid="inv-date-range-popover">
                      <Calendar
                        mode="range"
                        selected={dateRange}
                        onSelect={(r) => setDateRange(r || { from: undefined, to: undefined })}
                        numberOfMonths={1}
                        initialFocus
                      />
                      <div className="flex justify-between border-t border-industrial px-2 py-1.5 bg-surface-2">
                        <button
                          type="button"
                          data-testid="inv-date-range-clear"
                          onClick={() => setDateRange({ from: undefined, to: undefined })}
                          className="mono text-[10px] uppercase tracking-widest text-muted-industrial hover:text-primary-orange inline-flex items-center gap-1"
                        >
                          <X className="w-3 h-3" /> Clear
                        </button>
                        <button
                          type="button"
                          data-testid="inv-date-range-done"
                          onClick={() => setDateOpen(false)}
                          className="mono text-[10px] uppercase tracking-widest text-primary-orange"
                        >
                          Done
                        </button>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
            {customerId && (
              <div className="border border-industrial p-3 max-h-80 overflow-y-auto">
                <div className="flex items-center gap-2 mb-2">
                  <div className="label-tag">Uninvoiced Dockets</div>
                  <span className="mono text-[10px] uppercase tracking-widest text-muted-industrial ml-auto">
                    {dateRange.from
                      ? (dateRange.to && toISODate(dateRange.to) !== toISODate(dateRange.from)
                          ? `${fmt(dateRange.from)} → ${fmt(dateRange.to)}`
                          : fmt(dateRange.from))
                      : "All dates"}
                  </span>
                </div>
                {(() => {
                  const from = dateRange.from ? toISODate(dateRange.from) : null;
                  const to = dateRange.to ? toISODate(dateRange.to) : from;
                  const filtered = dockets.filter((d) => {
                    if (!from) return true;
                    const dd = parseDocketDate(d);
                    return dd >= from && dd <= to;
                  });
                  return (
                    <>
                      {filtered.length === 0 && dockets.length === 0 && (
                        <div className="text-xs text-muted-industrial">No delivered dockets to invoice for this customer.</div>
                      )}
                      {filtered.length === 0 && dockets.length > 0 && (
                        <div className="text-xs text-muted-industrial">No dockets fall inside the selected date range. Clear the filter to see all {dockets.length}.</div>
                      )}
                      {filtered.length > 0 && (() => {
                        const allSelected = filtered.every((d) => selected.includes(d.id));
                        const totalM3 = filtered.reduce((s, d) => s + Number(d.quantity_m3 || 0), 0);
                        const totalV = filtered.reduce((s, d) => s + Number(d.quantity_m3 || 0) * Number(d.price_per_m3 || 0), 0);
                        const toggle = () => {
                          const ids = filtered.map((d) => d.id);
                          if (allSelected) setSelected(selected.filter((x) => !ids.includes(x)));
                          else setSelected(Array.from(new Set([...selected, ...ids])));
                        };
                        return (
                          <button
                            type="button"
                            data-testid="inv-select-all"
                            onClick={toggle}
                            className={`w-full text-left border-2 px-3 py-2.5 mb-2 mono text-xs uppercase tracking-widest transition-colors flex items-center gap-3 ${
                              allSelected
                                ? "border-primary-orange bg-primary-orange/10 text-primary-orange"
                                : "border-primary-orange/40 text-primary-orange hover:border-primary-orange hover:bg-primary-orange/5"
                            }`}
                          >
                            <Checkbox checked={allSelected} className="pointer-events-none" />
                            <span className="font-bold">
                              {allSelected ? "Deselect" : "Select"} all {filtered.length} docket{filtered.length === 1 ? "" : "s"}
                            </span>
                            <span className="ml-auto normal-case tracking-normal text-[11px]">
                              {totalM3.toFixed(1)} m³ · ${totalV.toFixed(2)}
                            </span>
                          </button>
                        );
                      })()}
                      {filtered.map((d) => (
                        <label key={d.id} className="flex items-center gap-3 py-1.5 border-b border-industrial text-sm cursor-pointer">
                          <Checkbox data-testid={`inv-select-${d.docket_no}`} checked={selected.includes(d.id)} onCheckedChange={() => setSelected(selected.includes(d.id) ? selected.filter((x) => x !== d.id) : [...selected, d.id])} />
                          <span className="mono">{d.docket_no}</span>
                          <span className="mono text-xs">{d.mix_code}</span>
                          <span className="mono text-[10px] text-muted-industrial">{parseDocketDate(d)}</span>
                          <span className="mono text-xs text-muted-industrial ml-auto">{d.quantity_m3} m³ · ${(d.quantity_m3 * d.price_per_m3).toFixed(2)}</span>
                        </label>
                      ))}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
          <DialogFooter><Button data-testid="create-invoice-btn" onClick={create} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">Create Invoice</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-md" data-testid="email-invoice-dialog">
          <DialogHeader><DialogTitle className="heading uppercase">Email Invoice {emailInv?.invoice_no}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-industrial">
              Send invoice <span className="mono text-primary-orange">{emailInv?.invoice_no}</span> for <span className="mono">${emailInv?.total?.toFixed(2)}</span> to {emailInv?.customer_name} with the PDF attached.
            </div>
            <div>
              <div className="label-tag mb-1">Recipient Email</div>
              <Input data-testid="email-recipient-input" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="customer@example.com" className="rounded-none bg-transparent border-industrial" />
            </div>
            {emailInv?.last_emailed_at && (
              <div className="text-[10px] mono uppercase text-muted-industrial border-l-2 border-primary-orange pl-2">
                Last sent {emailInv.last_emailed_at.slice(0, 10)} → {emailInv.last_emailed_to}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="send-email-btn" onClick={sendEmail} disabled={emailSending} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">
              <Mail className="w-4 h-4 mr-1" /> {emailSending ? "Sending…" : "Send Now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* --- v3.4g · Daily bulk-invoice run dialog --- */
function DailyRunDialog({ open, onOpenChange, onDone }) {
  const [date, setDate] = useState(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("date");
    if (fromUrl) return fromUrl;
    const y = new Date(); y.setDate(y.getDate() - 1);
    const yy = y.getFullYear(); const mm = String(y.getMonth() + 1).padStart(2, "0"); const dd = String(y.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});   // v3.7 · {group_key: bool}
  const [running, setRunning] = useState(false);
  const [buckets, setBuckets] = useState([]);
  const [splittingId, setSplittingId] = useState("");

  const load = async (d) => {
    setLoading(true); setPreview(null);
    try {
      const [r, ba] = await Promise.all([
        api.get(`/invoices/daily-preview?date=${d}`),
        api.get("/admin/customer-bucket-audit").catch(() => ({ data: { buckets: [] } })),
      ]);
      setPreview(r.data);
      setBuckets(ba.data.buckets || []);
      // Default: select every eligible group (each row is one invoice)
      const s = {};
      (r.data.groups || []).forEach((g) => { s[g.group_key] = true; });
      setSelected(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load preview");
    } finally { setLoading(false); }
  };

  const splitBucket = async (b) => {
    if (!window.confirm(
      `Split "${b.name}" placeholder into ${b.uninvoiced_dockets} distinct customers?\n\n` +
      `Each job/docket currently under this bucket becomes its own walk-in customer ` +
      `(named from job.walk_in_customer, notes, or signed_by). ` +
      `Next daily run will produce ONE invoice per real customer.`
    )) return;
    setSplittingId(b.customer_id);
    try {
      const r = await api.post("/admin/split-bucket", {
        customer_id: b.customer_id,
        confirm: b.customer_id,
        dry_run: false,
        only_uninvoiced: true,
      });
      toast.success(
        `Split "${r.data.bucket_name}" → ${r.data.created_customers} customers · ${r.data.moved_dockets} dockets moved`,
        { duration: 8000 }
      );
      load(date);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Split failed");
    } finally { setSplittingId(""); }
  };

  useEffect(() => { if (open && date) load(date); /* eslint-disable-next-line */ }, [open, date]);

  const allGroups = preview?.groups || [];
  const chosen = allGroups.filter((g) => selected[g.group_key]);
  const totalM3 = chosen.reduce((s, g) => s + g.m3, 0);
  const totalV = chosen.reduce((s, g) => s + g.subtotal * 1.10, 0);   // + GST estimate
  const toggleAll = () => {
    if (!preview) return;
    const allOn = allGroups.every((g) => selected[g.group_key]);
    const s = {}; allGroups.forEach((g) => { s[g.group_key] = !allOn; });
    setSelected(s);
  };

  const run = async () => {
    if (chosen.length === 0) return toast.error("No invoices selected");
    if (!window.confirm(`Create ${chosen.length} invoice${chosen.length === 1 ? "" : "s"} for ${date}? This cannot be undone from the app.`)) return;
    setRunning(true);
    try {
      const r = await api.post("/invoices/daily-run", {
        date,
        group_keys: chosen.map((g) => g.group_key),
      });
      const created = r.data.created || [];
      const skipped = (r.data.skipped || []).filter((s) => s.reason && s.reason.startsWith("error"));
      if (created.length > 0) {
        toast.success(`${created.length} invoice${created.length === 1 ? "" : "s"} created · $${(r.data.total_value || 0).toFixed(2)} billed`);
      }
      if (skipped.length > 0) {
        const lines = skipped.slice(0, 4).map((s) => `• ${s.customer_name || s.group_key}: ${s.reason}`).join("\n");
        toast.error(`${skipped.length} row${skipped.length === 1 ? "" : "s"} skipped:\n${lines}${skipped.length > 4 ? "\n…" : ""}`, { duration: 12000 });
      }
      if (created.length === 0 && skipped.length === 0) {
        toast.warning("Nothing invoiced — no eligible dockets on that day");
      }
      if (created.length > 0) { onOpenChange(false); onDone?.(); }
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object") {
        const parts = [d.message || "Daily run failed"];
        if (d.hint) parts.push(d.hint);
        toast.error(parts.join(" · "), { duration: 9000 });
      } else {
        toast.error(d || "Daily run failed");
      }
    } finally { setRunning(false); }
  };

  const shift = (days) => {
    const d = new Date(date + "T00:00:00"); d.setDate(d.getDate() + days);
    const yy = d.getFullYear(); const mm = String(d.getMonth() + 1).padStart(2, "0"); const dd = String(d.getDate()).padStart(2, "0");
    setDate(`${yy}-${mm}-${dd}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-surface border-industrial max-w-3xl" data-testid="daily-run-dialog">
        <DialogHeader>
          <DialogTitle className="heading uppercase inline-flex items-center gap-2">
            <Zap className="w-4 h-4 text-primary-orange" /> Daily Invoice Run
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-xs text-muted-industrial">
            Picks up every <b className="text-ink">account customer</b> with uninvoiced delivered dockets for the chosen day
            and generates one invoice per customer in a single click. EFT-sale customers are skipped
            (they're already invoiced at the point of sale).
          </p>

          {/* Date navigator */}
          <div className="flex items-center gap-2">
            <button onClick={() => shift(-1)} data-testid="daily-prev-day" className="mono text-xs uppercase tracking-widest px-3 py-2 border border-industrial hover:border-primary-orange hover:text-primary-orange">← Prev</button>
            <Input
              data-testid="daily-run-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-none bg-transparent border-industrial mono flex-1"
            />
            <button onClick={() => shift(1)} data-testid="daily-next-day" className="mono text-xs uppercase tracking-widest px-3 py-2 border border-industrial hover:border-primary-orange hover:text-primary-orange">Next →</button>
          </div>

          {/* v3.6y — Warn if any placeholder walk-in customer ("EFT", "CASH")
              is bucketing multiple real customers under one invoice. */}
          {buckets.length > 0 && (
            <div className="border-2 border-amber-600 bg-amber-950/20 p-3" data-testid="bucket-warning">
              <div className="flex items-start gap-2">
                <span className="mono text-[10px] uppercase tracking-widest text-amber-400 font-bold shrink-0">⚠ Placeholder Customer</span>
                <div className="flex-1 mono text-[11px] text-ink">
                  {buckets.map((b) => (
                    <div key={b.customer_id} className="flex items-center gap-2 flex-wrap py-1 border-b border-amber-800/30 last:border-b-0" data-testid={`bucket-row-${b.customer_id}`}>
                      <span className="text-amber-300 font-bold">"{b.name}"</span>
                      <span className="opacity-70">holds</span>
                      <span className="text-amber-300 font-bold">{b.uninvoiced_dockets}</span>
                      <span className="opacity-70">uninvoiced dockets ({b.jobs} jobs) — all would bundle into ONE invoice. Split them out first:</span>
                      <Button
                        size="sm"
                        onClick={() => splitBucket(b)}
                        disabled={splittingId === b.customer_id}
                        data-testid={`split-bucket-${b.customer_id}`}
                        className="ml-auto rounded-none bg-amber-600 hover:bg-amber-500 text-black uppercase tracking-widest text-[10px] font-bold h-7 px-2"
                      >
                        {splittingId === b.customer_id ? "Splitting…" : `Split into ${b.jobs} customers`}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Preview */}
          <div className="border border-industrial max-h-96 overflow-y-auto">
            {loading && <div className="p-8 text-center label-tag text-primary-orange pulse-orange">Loading…</div>}
            {!loading && preview && allGroups.length === 0 && (
              <div className="p-6 text-center space-y-3" data-testid="daily-empty">
                <div className="mono text-xs uppercase tracking-widest text-muted-industrial">
                  No uninvoiced account-customer dockets for {date}
                </div>
                {preview.diagnosis && (preview.diagnosis.dockets_that_day > 0 || preview.diagnosis.archived > 0) && (
                  <div className="border-2 border-amber-600 bg-amber-950/20 p-3 text-left" data-testid="daily-empty-diagnosis">
                    <div className="uppercase tracking-widest text-amber-400 font-bold text-[10px] mb-2 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" />Diagnosis for {date}
                    </div>
                    <div className="mono text-[11px] text-ink space-y-1">
                      <div>· <b className="text-amber-300">{preview.diagnosis.dockets_that_day}</b> total docket{preview.diagnosis.dockets_that_day === 1 ? "" : "s"} exist on this day</div>
                      {preview.diagnosis.already_invoiced > 0 && (
                        <div>· <b className="text-emerald-300">{preview.diagnosis.already_invoiced}</b> already invoiced (open Recall Day to see the invoices)</div>
                      )}
                      {preview.diagnosis.not_delivered > 0 && (
                        <div>· <b className="text-red-300">{preview.diagnosis.not_delivered}</b> not delivered / cancelled — they'll never invoice</div>
                      )}
                      {preview.diagnosis.cash_sale > 0 && (
                        <div>· <b className="text-purple-300">{preview.diagnosis.cash_sale}</b> belong to EFT-SALE customers — invoiced at the point of sale</div>
                      )}
                      {preview.diagnosis.archived > 0 && (
                        <div>· <b className="text-amber-300">{preview.diagnosis.archived}</b> in test_history archive — restore via Recall Day → Restore Archived</div>
                      )}
                    </div>
                    <div className="mt-2 pt-2 border-t border-amber-800/40 mono text-[10px] text-muted-industrial">
                      Tip: if you just voided invoices, the released dockets may have loaded on a <b className="text-amber-300">different plant-local day</b>. Try adjacent dates or use <b className="text-emerald-300">Reset &amp; Rebuild</b> which auto-rebuilds every affected date.
                    </div>
                  </div>
                )}
              </div>
            )}
            {!loading && preview && allGroups.length > 0 && (
              <table className="w-full text-sm">
                <thead className="border-b border-industrial sticky top-0 bg-surface">
                  <tr className="text-left">
                    <th className="p-2 label-tag w-8">
                      <Checkbox
                        data-testid="daily-select-all"
                        checked={allGroups.every((g) => selected[g.group_key])}
                        onCheckedChange={toggleAll}
                      />
                    </th>
                    <th className="p-2 label-tag">Customer · Site · PO</th>
                    <th className="p-2 label-tag text-right">Dockets</th>
                    <th className="p-2 label-tag text-right">m³</th>
                    <th className="p-2 label-tag text-right">Subtotal</th>
                    <th className="p-2 label-tag text-right">Total ex GST +10%</th>
                  </tr>
                </thead>
                <tbody>
                  {allGroups.map((g) => {
                    const isSplit = g.group_reason?.startsWith("Split");
                    const isPerDocket = g.invoicing_mode === "split_by_docket";
                    return (
                      <tr key={g.group_key} data-testid={`daily-row-${g.group_key}`}
                          className={`border-b border-industrial cursor-pointer ${selected[g.group_key] ? "bg-primary-orange/5" : "hover:bg-surface-2"}`}
                          onClick={() => setSelected({ ...selected, [g.group_key]: !selected[g.group_key] })}>
                        <td className="p-2">
                          <Checkbox checked={!!selected[g.group_key]} className="pointer-events-none" />
                        </td>
                        <td className="p-2">
                          <div className="font-semibold" data-testid={`row-name-${g.group_key}`}>{g.customer_name || "—"}</div>
                          <div className="text-[10px] mono text-muted-industrial mt-0.5 space-x-1">
                            {g.site_name || g.site_address ? (
                              <span>📍 {g.site_name || g.site_address}</span>
                            ) : (
                              <span className="opacity-50">no site</span>
                            )}
                            {g.po_number && <span className="text-primary-orange">· PO {g.po_number}</span>}
                          </div>
                          <div className="mt-1 inline-block">
                            <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${
                              isPerDocket ? "border-purple-500 text-purple-400" :
                              isSplit     ? "border-amber-500 text-amber-400" :
                                            "border-emerald-500 text-emerald-400"
                            }`} data-testid={`group-badge-${g.group_key}`}>
                              {g.group_reason}
                            </span>
                          </div>
                          <div className="text-[10px] mono text-muted-industrial mt-1 truncate">
                            {g.docket_nos.slice(0, 6).join(" · ")}{g.docket_nos.length > 6 ? ` +${g.docket_nos.length - 6}` : ""}
                          </div>
                        </td>
                        <td className="p-2 mono text-right">{g.docket_count}</td>
                        <td className="p-2 mono text-right">{g.m3.toFixed(1)}</td>
                        <td className="p-2 mono text-right">${g.subtotal.toFixed(2)}</td>
                        <td className="p-2 mono text-right font-bold text-primary-orange">${(g.subtotal * 1.10).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Summary bar */}
          {preview && allGroups.length > 0 && (
            <div className="border-2 border-primary-orange bg-primary-orange/5 p-3 mono text-xs uppercase tracking-widest flex items-center justify-between" data-testid="daily-summary">
              <div>
                <span className="text-primary-orange font-bold">{chosen.length}</span> invoice{chosen.length === 1 ? "" : "s"} ·
                <span className="text-primary-orange font-bold ml-1">{totalM3.toFixed(1)}</span> m³
              </div>
              <div className="text-primary-orange font-bold text-base normal-case tracking-normal">
                ${totalV.toFixed(2)} billed
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            data-testid="daily-run-confirm"
            onClick={run}
            disabled={running || chosen.length === 0}
            className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold disabled:opacity-40"
          >
            <Zap className="w-4 h-4 mr-1" />
            {running ? "Running…" : `Create ${chosen.length || 0} invoice${chosen.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ── Inline customer-name editor · v3.6y ─────────────────────────────
// Pencil icon on hover reveals an inline input. On save we PATCH the
// customer record and cascade the rename to uninvoiced dockets + jobs
// so the daily-run invoice will show the corrected name.
function CustomerNameCell({ customer, onRenamed }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(customer.customer_name || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(customer.customer_name || ""); }, [customer.customer_name]);

  const save = async (e) => {
    e?.stopPropagation();
    const nn = val.trim();
    if (nn === (customer.customer_name || "").trim()) { setEditing(false); return; }
    setSaving(true);
    try {
      const r = await api.patch(`/customers/${customer.customer_id}/rename`, { name: nn });
      toast.success(`Renamed → ${r.data.new_name} (${r.data.dockets_renamed} docket${r.data.dockets_renamed === 1 ? "" : "s"} updated)`);
      onRenamed(r.data.new_name);
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <Input
          data-testid={`rename-input-${customer.customer_id}`}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(e); if (e.key === "Escape") { setEditing(false); setVal(customer.customer_name || ""); } }}
          autoFocus
          className="rounded-none bg-transparent border-industrial mono h-7 py-0"
        />
        <Button
          size="sm"
          onClick={save}
          disabled={saving}
          data-testid={`rename-save-${customer.customer_id}`}
          className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black h-7 px-2"
        >
          <Check className="w-3.5 h-3.5" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => { e.stopPropagation(); setEditing(false); setVal(customer.customer_name || ""); }}
          className="rounded-none border-industrial bg-transparent h-7 px-2"
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
    );
  }
  return (
    <div className="group flex items-center gap-2">
      <div className="font-semibold" data-testid={`row-name-${customer.customer_id}`}>{customer.customer_name}</div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        className="opacity-0 group-hover:opacity-100 transition text-muted-industrial hover:text-primary-orange"
        data-testid={`rename-edit-${customer.customer_id}`}
        title="Rename customer"
      >
        <Pencil className="w-3 h-3" />
      </button>
    </div>
  );
}


/* -----------------------------------------------------------
 * v3.8 · Recall Day dialog — read-only diagnostic snapshot of
 * every docket + invoice (live AND archived) that touched a
 * plant-local calendar day. Powers the "I can't find yesterday's
 * invoices" workflow without needing to SSH into Mongo.
 * --------------------------------------------------------- */
function RecallDayDialog({ open, onOpenChange, onDone, openReset, openRebuild }) {
  const [date, setDate] = useState(() => {
    const y = new Date(); y.setDate(y.getDate() - 1);
    const yy = y.getFullYear(); const mm = String(y.getMonth() + 1).padStart(2, "0"); const dd = String(y.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  });
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("dockets");   // dockets | invoices
  const [restoring, setRestoring] = useState(false);

  const load = async (d) => {
    setLoading(true); setSnap(null);
    try {
      const r = await api.get(`/admin/day-recall?date=${d}`);
      setSnap(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recall failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && date) load(date); /* eslint-disable-next-line */ }, [open, date]);

  const shift = (days) => {
    const d = new Date(date + "T00:00:00"); d.setDate(d.getDate() + days);
    const yy = d.getFullYear(); const mm = String(d.getMonth() + 1).padStart(2, "0"); const dd = String(d.getDate()).padStart(2, "0");
    setDate(`${yy}-${mm}-${dd}`);
  };

  const restoreArchived = async () => {
    if (!snap) return;
    const arch = (snap.summary.archived_dockets.count || 0) + (snap.summary.archived_invoices.count || 0);
    if (arch === 0) return toast.info("Nothing archived for this date");
    if (!window.confirm(`Restore ${snap.summary.archived_dockets.count} archived dockets + ${snap.summary.archived_invoices.count} archived invoices from ${date} back into the live database?\n\nArchive audit copies are kept.`)) return;
    setRestoring(true);
    try {
      const r = await api.post("/admin/restore-day", {
        date,
        confirm: `RESTORE-${date}`,
        dry_run: false,
      });
      const c = r.data.counts || {};
      const restored = Object.entries(c).map(([k, v]) => `${k}:${v.restored}`).join(" · ");
      toast.success(`Restored → ${restored || "0 rows"}`, { duration: 9000 });
      await load(date);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setRestoring(false); }
  };

  const sum = snap?.summary || {};
  const liveDockets = snap?.live_dockets || [];
  const archDockets = snap?.archived_dockets || [];
  const liveInvs = snap?.live_invoices || [];
  const archInvs = snap?.archived_invoices || [];
  const coveringInvs = snap?.covering_invoices || [];
  // v3.9 · dedupe: covering invoices that AREN'T already in live_invoices
  // (i.e., invoices issued on a different plant-local day)
  const liveInvIds = new Set(liveInvs.map((i) => i.id));
  const coveringOnly = coveringInvs.filter((i) => !liveInvIds.has(i.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-surface border-industrial max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="recall-day-dialog">
        <DialogHeader>
          <DialogTitle className="heading uppercase inline-flex items-center gap-2">
            <Search className="w-4 h-4 text-amber-400" /> Recall Day
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-xs text-muted-industrial">
            Diagnostic snapshot of everything that happened on a plant-local calendar day —
            live dockets, live invoices, plus anything archived to <span className="mono">test_history</span> that
            can be pulled back. Read-only; use <b className="text-red-400">Reset Day</b> to void invoices.
          </p>

          {/* Date navigator */}
          <div className="flex items-center gap-2">
            <button onClick={() => shift(-1)} data-testid="recall-prev-day" className="mono text-xs uppercase tracking-widest px-3 py-2 border border-industrial hover:border-amber-500 hover:text-amber-400">← Prev</button>
            <Input
              data-testid="recall-date-input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-none bg-transparent border-industrial mono flex-1"
            />
            <button onClick={() => shift(1)} data-testid="recall-next-day" className="mono text-xs uppercase tracking-widest px-3 py-2 border border-industrial hover:border-amber-500 hover:text-amber-400">Next →</button>
            <button onClick={() => load(date)} disabled={loading} data-testid="recall-reload-btn" className="mono text-xs uppercase tracking-widest px-3 py-2 border border-industrial hover:border-primary-orange hover:text-primary-orange">
              <RefreshCcw className={`w-3.5 h-3.5 inline mr-1 ${loading ? "animate-spin" : ""}`} />Reload
            </button>
          </div>

          {/* Summary grid */}
          {loading && <div className="text-center py-6 text-muted-industrial mono text-xs uppercase" data-testid="recall-loading">Loading…</div>}
          {snap && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="recall-summary">
                <SummaryCell label="Live Dockets" value={sum.live_dockets?.count || 0} sub={`${(sum.live_dockets?.m3 || 0).toFixed(1)} m³`} />
                <SummaryCell label="Delivered" value={sum.delivered?.count || 0} sub={`${(sum.delivered?.m3 || 0).toFixed(1)} m³`} tone="ok" />
                <SummaryCell label="Cancelled" value={sum.cancelled?.count || 0} sub={`${(sum.cancelled?.m3 || 0).toFixed(1)} m³`} tone={sum.cancelled?.count ? "warn" : "muted"} />
                <SummaryCell label="Uninvoiced" value={sum.uninvoiced_delivered?.count || 0} sub={`$${(sum.uninvoiced_delivered?.value || 0).toFixed(2)}`} tone={sum.uninvoiced_delivered?.count ? "warn" : "muted"} testid="recall-uninvoiced-cell" />
                <SummaryCell label="Live Invoices" value={sum.live_invoices?.count || 0} sub={`$${(sum.live_invoices?.value || 0).toFixed(2)}`} tone={sum.live_invoices?.count ? "ok" : "muted"} testid="recall-live-invoices-cell" />
                <SummaryCell label="Covering" value={sum.covering_invoices?.count || 0} sub={`$${(sum.covering_invoices?.value || 0).toFixed(2)}`} tone={sum.covering_invoices?.count ? "ok" : "muted"} testid="recall-covering-invoices-cell" />
                <SummaryCell label="Paid" value={sum.covering_invoices?.paid_count || 0} sub="covering" tone={sum.covering_invoices?.paid_count ? "ok" : "muted"} />
                <SummaryCell label="Archived" value={(sum.archived_dockets?.count || 0) + (sum.archived_invoices?.count || 0)} sub={`${sum.archived_dockets?.count || 0} d + ${sum.archived_invoices?.count || 0} i`} tone={((sum.archived_dockets?.count || 0) + (sum.archived_invoices?.count || 0)) ? "warn" : "muted"} testid="recall-archived-cell" />
              </div>

              {/* Archived warning + restore CTA */}
              {((sum.archived_dockets?.count || 0) + (sum.archived_invoices?.count || 0)) > 0 && (
                <div className="border-2 border-amber-600 bg-amber-950/20 p-3 flex items-start gap-2" data-testid="recall-archive-banner">
                  <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 text-[11px] mono text-ink">
                    <div className="text-amber-300 font-bold uppercase tracking-widest mb-1">Archived rows found for {date}</div>
                    This day has <b className="text-amber-300">{sum.archived_dockets?.count || 0} dockets</b> and <b className="text-amber-300">{sum.archived_invoices?.count || 0} invoices</b> sitting in the audit archive.
                    They likely got moved during a Live Launch or manual purge — click Restore to bring them back into the live database. Audit copies stay in place.
                  </div>
                  <Button onClick={restoreArchived} disabled={restoring} data-testid="recall-restore-btn" className="rounded-none bg-amber-500 hover:bg-amber-400 text-black uppercase tracking-widest text-[10px] font-bold shrink-0">
                    <ArchiveRestore className="w-3.5 h-3.5 mr-1" />{restoring ? "Restoring…" : "Restore Archived"}
                  </Button>
                </div>
              )}

              {/* Tabs */}
              <div className="flex items-center gap-1 border-b border-industrial">
                <button onClick={() => setTab("dockets")} data-testid="recall-tab-dockets" className={`mono text-[11px] uppercase tracking-widest px-4 py-2 border-b-2 ${tab === "dockets" ? "border-amber-400 text-amber-400" : "border-transparent text-muted-industrial hover:text-ink"}`}>
                  Dockets ({liveDockets.length + archDockets.length})
                </button>
                <button onClick={() => setTab("invoices")} data-testid="recall-tab-invoices" className={`mono text-[11px] uppercase tracking-widest px-4 py-2 border-b-2 ${tab === "invoices" ? "border-amber-400 text-amber-400" : "border-transparent text-muted-industrial hover:text-ink"}`}>
                  Invoices ({liveInvs.length + coveringOnly.length + archInvs.length})
                </button>
              </div>

              {tab === "dockets" && (
                <div className="max-h-[420px] overflow-y-auto border border-industrial">
                  <table className="w-full text-[11px] mono">
                    <thead className="sticky top-0 bg-surface-2 border-b border-industrial">
                      <tr className="text-left">
                        <th className="p-2 label-tag">Docket</th>
                        <th className="p-2 label-tag">Customer</th>
                        <th className="p-2 label-tag">Mix</th>
                        <th className="p-2 label-tag text-right">m³</th>
                        <th className="p-2 label-tag">Status</th>
                        <th className="p-2 label-tag">Loaded</th>
                        <th className="p-2 label-tag">Invoice</th>
                        <th className="p-2 label-tag text-right">Total</th>
                        <th className="p-2 label-tag text-right">PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {liveDockets.map((d) => (
                        <RecallDocketRow key={"L" + d.id} d={d} archived={false} />
                      ))}
                      {archDockets.map((d) => (
                        <RecallDocketRow key={"A" + d.id} d={d} archived={true} />
                      ))}
                      {(liveDockets.length + archDockets.length) === 0 && (
                        <tr><td colSpan="9" className="p-6 text-center text-muted-industrial">No dockets on {date}.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {tab === "invoices" && (
                <div className="max-h-[420px] overflow-y-auto border border-industrial">
                  <table className="w-full text-[11px] mono">
                    <thead className="sticky top-0 bg-surface-2 border-b border-industrial">
                      <tr className="text-left">
                        <th className="p-2 label-tag">Invoice</th>
                        <th className="p-2 label-tag">Customer</th>
                        <th className="p-2 label-tag">Kind</th>
                        <th className="p-2 label-tag">Site / PO</th>
                        <th className="p-2 label-tag text-right">Subtotal</th>
                        <th className="p-2 label-tag text-right">GST</th>
                        <th className="p-2 label-tag text-right">Total</th>
                        <th className="p-2 label-tag">Status</th>
                        <th className="p-2 label-tag">Issued</th>
                        <th className="p-2 label-tag text-right">PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {liveInvs.map((i) => (
                        <RecallInvoiceRow key={"L" + i.id} i={i} archived={false} covering={false} />
                      ))}
                      {coveringOnly.map((i) => (
                        <RecallInvoiceRow key={"C" + i.id} i={i} archived={false} covering={true} />
                      ))}
                      {archInvs.map((i) => (
                        <RecallInvoiceRow key={"A" + i.id} i={i} archived={true} covering={false} />
                      ))}
                      {(liveInvs.length + coveringOnly.length + archInvs.length) === 0 && (
                        <tr><td colSpan="10" className="p-6 text-center text-muted-industrial" data-testid="recall-no-invoices">No invoices issued on {date}.</td></tr>
                      )}
                    </tbody>
                  </table>
                  {coveringOnly.length > 0 && (
                    <div className="p-2 border-t border-industrial bg-amber-950/10">
                      <div className="mono text-[10px] text-amber-400 uppercase tracking-widest">◈ Covering invoices (issued on a different day but cover a docket from {date})</div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <button onClick={() => { onOpenChange(false); openRebuild?.(date); }} data-testid="recall-open-rebuild-btn" className="mono text-[10px] uppercase tracking-widest px-3 py-2 border border-emerald-600 text-emerald-400 hover:bg-emerald-600 hover:text-black">
            <RefreshCcw className="w-3.5 h-3.5 inline mr-1" />Reset &amp; Rebuild
          </button>
          <button onClick={() => { onOpenChange(false); openReset?.(date); }} data-testid="recall-open-reset-btn" className="mono text-[10px] uppercase tracking-widest px-3 py-2 border border-red-600 text-red-400 hover:bg-red-600 hover:text-white">
            <Undo2 className="w-3.5 h-3.5 inline mr-1" />Reset This Day
          </button>
          <Button onClick={() => onOpenChange(false)} data-testid="recall-close-btn" className="rounded-none bg-surface-2 border border-industrial hover:border-primary-orange uppercase tracking-widest text-xs">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryCell({ label, value, sub, tone = "muted", testid }) {
  const tones = {
    muted: "text-muted-industrial border-industrial",
    ok: "text-emerald-400 border-emerald-700",
    warn: "text-amber-400 border-amber-700",
  };
  return (
    <div className={`border ${tones[tone] || tones.muted} p-2`} data-testid={testid || `sum-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="label-tag">{label}</div>
      <div className={`text-2xl font-black mono ${tone === "muted" ? "text-ink" : ""}`}>{value}</div>
      <div className="text-[10px] mono opacity-70">{sub}</div>
    </div>
  );
}

function RecallDocketRow({ d, archived }) {
  const st = (d.status || "").toLowerCase();
  const stCol = st === "delivered" ? "text-emerald-400" : st === "cancelled" ? "text-red-400" : "text-amber-400";
  return (
    <tr className={`border-b border-industrial/40 ${archived ? "bg-amber-950/10" : ""}`} data-testid={`recall-docket-${d.docket_no || d.id}`}>
      <td className="p-2">
        {archived && <span className="text-[9px] text-amber-400 mr-1" title="Archived in test_history">◈</span>}
        {d.docket_no || d.id?.slice(0, 8)}
      </td>
      <td className="p-2 text-ink">{d.customer_name || "—"}</td>
      <td className="p-2">{d.mix_code || "—"}</td>
      <td className="p-2 text-right">{(d.quantity_m3 || 0).toFixed(1)}</td>
      <td className={`p-2 uppercase ${stCol}`}>{d.status || "—"}</td>
      <td className="p-2 opacity-70">{(d.loaded_at || "").slice(11, 16) || "—"}</td>
      <td className="p-2 opacity-80">{d.invoice_ref || <span className="text-red-400">— none —</span>}</td>
      <td className="p-2 text-right text-primary-orange">${(d.total || d.grand_total || 0).toFixed(2)}</td>
      <td className="p-2 text-right">
        {!archived && (
          <a href={apiHref(`/dockets/${d.id}/pdf`)} target="_blank" rel="noreferrer" data-testid={`recall-docket-pdf-${d.docket_no || d.id}`} className="text-primary-orange hover:underline inline-flex items-center gap-0.5 text-[10px] uppercase">
            <Printer className="w-3 h-3" /><ExternalLink className="w-2.5 h-2.5" />
          </a>
        )}
      </td>
    </tr>
  );
}

function RecallInvoiceRow({ i, archived, covering }) {
  const st = (i.status || "").toLowerCase();
  const stCol = st === "paid" ? "text-emerald-400" : st === "voided" ? "text-red-400" : st === "unpaid" ? "text-amber-400" : "text-muted-industrial";
  const bg = archived ? "bg-amber-950/10" : covering ? "bg-amber-950/5" : "";
  return (
    <tr className={`border-b border-industrial/40 ${bg}`} data-testid={`recall-invoice-${i.invoice_no || i.id}`}>
      <td className="p-2">
        {archived && <span className="text-[9px] text-amber-400 mr-1" title="Archived in test_history">◈</span>}
        {covering && !archived && <span className="text-[9px] text-amber-400 mr-1" title="Issued on a different day but covers a docket from this day">◈</span>}
        {i.invoice_no}
      </td>
      <td className="p-2 text-ink">{i.customer_name || "—"}</td>
      <td className="p-2 opacity-70 uppercase text-[10px]">{i.kind || "invoice"}</td>
      <td className="p-2 opacity-70 text-[10px]">{i.site_name || ""}{i.po_number ? ` · PO ${i.po_number}` : ""}</td>
      <td className="p-2 text-right">${(i.subtotal || 0).toFixed(2)}</td>
      <td className="p-2 text-right">${(i.tax || 0).toFixed(2)}</td>
      <td className="p-2 text-right text-primary-orange font-bold">${(i.total || 0).toFixed(2)}</td>
      <td className={`p-2 uppercase ${stCol}`}>{i.status || "—"}</td>
      <td className="p-2 opacity-70">{i.issued_at ? (() => { const dt = new Date(i.issued_at); return isNaN(dt) ? (i.issued_at || "").slice(0, 10) : `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`; })() : ""}</td>
      <td className="p-2 text-right">
        {!archived && (
          <a href={apiHref(`/invoices/${i.id}/pdf`)} target="_blank" rel="noreferrer" data-testid={`recall-invoice-pdf-${i.invoice_no}`} className="text-primary-orange hover:underline inline-flex items-center gap-0.5 text-[10px] uppercase">
            <Printer className="w-3 h-3" /><ExternalLink className="w-2.5 h-2.5" />
          </a>
        )}
      </td>
    </tr>
  );
}


/* -----------------------------------------------------------
 * v3.8 · Reset Day dialog — destructive wizard that voids every
 * invoice issued on a plant-local calendar day, releasing the
 * dockets so the daily-run can be re-executed cleanly. Guards:
 * dry-run preview → typed confirm token → optional include-paid
 * → jump straight into Daily Invoice Run when done.
 * --------------------------------------------------------- */
function ResetDayDialog({ open, onOpenChange, initialDate, onDone, openDailyRun }) {
  const [date, setDate] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [includePaid, setIncludePaid] = useState(false);
  const [matchMode, setMatchMode] = useState("docket_day");   // v3.9: "docket_day" | "issued"
  const [confirmText, setConfirmText] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const defaultDate = () => {
    const y = new Date(); y.setDate(y.getDate() - 1);
    const yy = y.getFullYear(); const mm = String(y.getMonth() + 1).padStart(2, "0"); const dd = String(y.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  };

  useEffect(() => {
    if (open) {
      setDate(initialDate || defaultDate());
      setPreview(null);
      setResult(null);
      setConfirmText("");
      setIncludePaid(false);
      setMatchMode("docket_day");
    }
  }, [open, initialDate]);

  const dryRun = async () => {
    setLoading(true); setPreview(null);
    try {
      const r = await api.post("/invoices/void-day", {
        date,
        confirm: "preview",
        dry_run: true,
        include_paid: includePaid,
        match: matchMode,
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry-run failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && date) dryRun(); /* eslint-disable-next-line */ }, [open, date, includePaid, matchMode]);

  const expectedConfirm = `RESET-${date}`;
  const canRun = preview && (preview.voided_count > 0) && confirmText === expectedConfirm && !running;

  const run = async () => {
    if (!canRun) return;
    setRunning(true);
    try {
      const r = await api.post("/invoices/void-day", {
        date,
        confirm: `VOID-${date}`,
        dry_run: false,
        include_paid: includePaid,
        match: matchMode,
      });
      setResult(r.data);
      toast.success(`Voided ${r.data.voided_count} invoice${r.data.voided_count === 1 ? "" : "s"} · ${r.data.dockets_released} dockets released · $${(r.data.money_voided || 0).toFixed(2)}`, { duration: 8000 });
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setRunning(false); }
  };

  const shift = (days) => {
    const d = new Date(date + "T00:00:00"); d.setDate(d.getDate() + days);
    const yy = d.getFullYear(); const mm = String(d.getMonth() + 1).padStart(2, "0"); const dd = String(d.getDate()).padStart(2, "0");
    setDate(`${yy}-${mm}-${dd}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-surface border-2 border-red-600 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="reset-day-dialog">
        <DialogHeader>
          <DialogTitle className="heading uppercase inline-flex items-center gap-2 text-red-400">
            <Undo2 className="w-4 h-4" /> Reset Day · Void Invoices
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="border-l-4 border-red-600 bg-red-950/20 pl-3 py-2 text-[11px] mono text-ink">
            <div className="uppercase tracking-widest text-red-400 font-bold text-[10px] mb-1">Destructive · but safe</div>
            Every unpaid invoice issued on the chosen day is <b className="text-red-300">voided</b> and its dockets are <b className="text-red-300">released</b> back
            into "uninvoiced". Once done, you can re-run <b className="text-primary-orange">Daily Invoice Run</b> to rebuild them
            with corrected data. Paid invoices are protected — tick <span className="text-amber-400">Include paid</span> only if you've refunded them first.
          </div>

          {/* Date navigator */}
          <div className="flex items-center gap-2">
            <button onClick={() => shift(-1)} data-testid="reset-prev-day" className="mono text-xs uppercase tracking-widest px-3 py-2 border border-industrial hover:border-red-500 hover:text-red-400">← Prev</button>
            <Input
              data-testid="reset-date-input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-none bg-transparent border-industrial mono flex-1"
            />
            <button onClick={() => shift(1)} data-testid="reset-next-day" className="mono text-xs uppercase tracking-widest px-3 py-2 border border-industrial hover:border-red-500 hover:text-red-400">Next →</button>
          </div>

          {/* Include paid toggle */}
          <label className="flex items-center gap-2 text-[11px] mono uppercase tracking-widest cursor-pointer" data-testid="reset-include-paid-label">
            <Checkbox checked={includePaid} onCheckedChange={setIncludePaid} data-testid="reset-include-paid-cb" />
            <span className={includePaid ? "text-amber-400" : "text-muted-industrial"}>Include paid invoices (refund first!)</span>
          </label>

          {/* v3.9 · Match mode toggle */}
          <div className="border border-industrial p-2">
            <div className="label-tag mb-1.5">Match invoices by</div>
            <div className="grid grid-cols-2 gap-1" data-testid="reset-match-mode">
              <button
                type="button"
                onClick={() => setMatchMode("docket_day")}
                data-testid="reset-match-docket-day"
                className={`p-2 text-left border ${matchMode === "docket_day" ? "border-red-500 bg-red-950/20 text-red-300" : "border-industrial text-muted-industrial hover:text-ink"}`}
              >
                <div className="mono text-[10px] uppercase tracking-widest font-bold">Docket Day (Recommended)</div>
                <div className="mono text-[9px] opacity-70 mt-0.5">Voids every invoice that has a docket LOADED on {date}. Catches invoices generated by the next-morning daily-run.</div>
              </button>
              <button
                type="button"
                onClick={() => setMatchMode("issued")}
                data-testid="reset-match-issued"
                className={`p-2 text-left border ${matchMode === "issued" ? "border-red-500 bg-red-950/20 text-red-300" : "border-industrial text-muted-industrial hover:text-ink"}`}
              >
                <div className="mono text-[10px] uppercase tracking-widest font-bold">Invoice Issued Date</div>
                <div className="mono text-[9px] opacity-70 mt-0.5">Legacy: voids only invoices <b>issued</b> on {date}. Miss invoices where the pour was on {date} but the invoice was generated the next day.</div>
              </button>
            </div>
          </div>

          {loading && <div className="text-center py-4 text-muted-industrial mono text-xs uppercase">Loading preview…</div>}

          {/* Preview */}
          {preview && !result && (
            <>
              <div className="grid grid-cols-4 gap-2" data-testid="reset-preview-summary">
                <SummaryCell label="Invoices" value={preview.voided_count} sub="will be voided" tone={preview.voided_count ? "warn" : "muted"} testid="reset-preview-voided" />
                <SummaryCell label="Dockets" value={preview.dockets_released} sub="will be released" tone={preview.dockets_released ? "warn" : "muted"} testid="reset-preview-released" />
                <SummaryCell label="Value" value={`$${(preview.money_voided || 0).toFixed(0)}`} sub="ex-tax total" tone={preview.money_voided ? "warn" : "muted"} />
                <SummaryCell label="Skipped" value={preview.skipped_count || 0} sub="already voided/paid" tone={preview.skipped_count ? "muted" : "muted"} />
              </div>

              {preview.voided_count === 0 && (
                <div className="border border-industrial p-4 text-center text-muted-industrial mono text-xs uppercase" data-testid="reset-nothing-to-do">
                  Nothing to void for {date}. {preview.skipped_count > 0 ? `${preview.skipped_count} paid/already-voided invoices left alone.` : ""}
                </div>
              )}

              {preview.voided_count > 0 && (
                <>
                  <div className="max-h-[240px] overflow-y-auto border border-industrial">
                    <table className="w-full text-[11px] mono">
                      <thead className="sticky top-0 bg-surface-2 border-b border-industrial">
                        <tr className="text-left">
                          <th className="p-2 label-tag">Invoice</th>
                          <th className="p-2 label-tag">Customer</th>
                          <th className="p-2 label-tag text-right">Total</th>
                          <th className="p-2 label-tag text-right">Dockets Released</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(preview.voided || []).map((v, idx) => (
                          <tr key={idx} className="border-b border-industrial/40" data-testid={`reset-preview-row-${v.invoice_no}`}>
                            <td className="p-2">{v.invoice_no}</td>
                            <td className="p-2 text-ink">{v.customer_name}</td>
                            <td className="p-2 text-right text-primary-orange">${(v.total || 0).toFixed(2)}</td>
                            <td className="p-2 text-right">{v.dockets_to_release || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="border border-red-700 bg-red-950/10 p-3">
                    <div className="label-tag text-red-400 mb-2">Type this to confirm</div>
                    <div className="flex items-center gap-2">
                      <span className="mono text-xs bg-surface-2 border border-industrial px-2 py-1.5 text-red-300 select-all">{expectedConfirm}</span>
                      <Input
                        data-testid="reset-confirm-input"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder={`Type ${expectedConfirm}`}
                        className="rounded-none bg-transparent border-industrial mono flex-1"
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* Result */}
          {result && (
            <div className="border-2 border-emerald-600 bg-emerald-950/20 p-4 space-y-2" data-testid="reset-result">
              <div className="label-tag text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Reset complete</div>
              <div className="mono text-xs text-ink">
                <b className="text-emerald-300">{result.voided_count}</b> invoice{result.voided_count === 1 ? "" : "s"} voided ·
                <b className="text-emerald-300"> {result.dockets_released}</b> dockets released ·
                <b className="text-emerald-300"> ${(result.money_voided || 0).toFixed(2)}</b> freed.
              </div>
              <div className="text-[11px] text-muted-industrial">
                {date} is now clean. You can jump straight into the Daily Invoice Run to rebuild.
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {!result && (
            <Button
              onClick={run}
              disabled={!canRun}
              data-testid="reset-execute-btn"
              className="rounded-none bg-red-600 hover:bg-red-500 text-white uppercase tracking-widest text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4 mr-1" />{running ? "Resetting…" : `Void ${preview?.voided_count || 0} Invoices`}
            </Button>
          )}
          {result && (
            <Button
              onClick={openDailyRun}
              data-testid="reset-open-daily-run-btn"
              className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase tracking-widest text-xs font-bold"
            >
              <Zap className="w-4 h-4 mr-1" />Run Daily Invoice
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)} data-testid="reset-close-btn" className="rounded-none bg-surface-2 border border-industrial hover:border-primary-orange uppercase tracking-widest text-xs">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* -----------------------------------------------------------
 * v3.8 · Purge Trial Data dialog — one-shot archive of every
 * operational document dated on-or-before the chosen date.
 * Wraps /admin/purge-before with a live count preview,
 * per-collection checkbox filter, and PURGE-BEFORE-<date>
 * confirmation token. Non-destructive: rows are moved into
 * `test_history` (audit archive), not hard-deleted.
 * --------------------------------------------------------- */
const PURGE_KINDS = [
  { key: "invoice",       label: "Invoices",         critical: true },
  { key: "docket",        label: "Dockets",          critical: true },
  { key: "job",           label: "Jobs",             critical: true },
  { key: "batch",         label: "Batches",          critical: false },
  { key: "cash_sale",     label: "EFT Sales",        critical: false },
  { key: "payment",       label: "Payments",         critical: false },
  { key: "topup",         label: "Top-ups",          critical: false },
  { key: "quote_request", label: "Quote Requests",   critical: false },
];

function PurgeTrialDialog({ open, onOpenChange, onDone }) {
  const [fromDate, setFromDate] = useState("");   // optional (empty = since forever)
  const [date, setDate] = useState(() => {
    const y = new Date(); y.setDate(y.getDate() - 1);
    const yy = y.getFullYear(); const mm = String(y.getMonth() + 1).padStart(2, "0"); const dd = String(y.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  });
  const [enabled, setEnabled] = useState(() => {
    const s = {}; PURGE_KINDS.forEach((k) => { s[k.key] = true; }); return s;
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setPreview(null); setResult(null); setConfirmText("");
    }
  }, [open]);

  const activeKinds = PURGE_KINDS.filter((k) => enabled[k.key]).map((k) => k.key);

  const loadPreview = async () => {
    if (!date || activeKinds.length === 0) { setPreview({ total: 0, scope: {}, date, from_date: fromDate || null, kinds: [] }); return; }
    setLoading(true); setPreview(null);
    try {
      const qs = new URLSearchParams({ date, kinds: activeKinds.join(",") });
      if (fromDate) qs.set("from_date", fromDate);
      const r = await api.get(`/admin/purge-before-preview?${qs.toString()}`);
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && date) loadPreview(); /* eslint-disable-next-line */ }, [open, date, fromDate, JSON.stringify(enabled)]);

  const expectedConfirm = `PURGE-BEFORE-${date}`;
  const canRun = preview && (preview.total > 0) && confirmText === expectedConfirm && !running && (!fromDate || fromDate <= date);

  const run = async () => {
    if (!canRun) return;
    setRunning(true);
    try {
      const r = await api.post("/admin/purge-before", {
        date,
        from_date: fromDate || null,
        kinds: activeKinds,
        confirm: expectedConfirm,
        dry_run: false,
      });
      setResult(r.data);
      toast.success(`Archived ${r.data.total_moved} rows${r.data.total_value_moved > 0 ? ` · $${r.data.total_value_moved.toFixed(2)} of trial invoicing` : ""}. Restore any day via Recall Day.`, { duration: 10000 });
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally { setRunning(false); }
  };

  const shift = (setter, base, days) => {
    if (!base) return;
    const d = new Date(base + "T00:00:00"); d.setDate(d.getDate() + days);
    const yy = d.getFullYear(); const mm = String(d.getMonth() + 1).padStart(2, "0"); const dd = String(d.getDate()).padStart(2, "0");
    setter(`${yy}-${mm}-${dd}`);
  };

  const scopeOf = (kind) => preview?.scope?.[
    { invoice: "invoices", docket: "dockets", job: "jobs", batch: "batches",
      cash_sale: "cash_sales", payment: "payments", topup: "topups",
      quote_request: "quote_requests" }[kind]
  ];

  const rangeInvalid = fromDate && fromDate > date;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-surface border-2 border-fuchsia-600 max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="purge-trial-dialog">
        <DialogHeader>
          <DialogTitle className="heading uppercase inline-flex items-center gap-2 text-fuchsia-400">
            <Trash2 className="w-4 h-4" /> Archive Trial Range
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="border-l-4 border-fuchsia-600 bg-fuchsia-950/20 pl-3 py-2 text-[11px] mono text-ink">
            <div className="uppercase tracking-widest text-fuchsia-400 font-bold text-[10px] mb-1">Archive, not destroy</div>
            Every selected record dated within the range is moved into the audit archive
            (<span className="mono">test_history</span>) and removed from the live database. Reference data — customers,
            mixes, trucks, drivers, users, settings, price rules — is <b className="text-emerald-300">left alone</b>. Live
            invoice numbering continues from where it is now (no reset). Recovery: use <b className="text-amber-400">Recall Day</b> → Restore Archived.
          </div>

          {/* Date range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="label-tag mb-1">From (inclusive · optional)</div>
              <div className="flex items-center gap-1">
                <button onClick={() => shift(setFromDate, fromDate || date, -1)} data-testid="purge-from-prev" className="mono text-xs uppercase tracking-widest px-2 py-2 border border-industrial hover:border-fuchsia-500 hover:text-fuchsia-400">←</button>
                <Input
                  data-testid="purge-from-date-input"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  placeholder="since forever"
                  className="rounded-none bg-transparent border-industrial mono flex-1"
                />
                <button onClick={() => shift(setFromDate, fromDate || date, 1)} data-testid="purge-from-next" className="mono text-xs uppercase tracking-widest px-2 py-2 border border-industrial hover:border-fuchsia-500 hover:text-fuchsia-400">→</button>
                {fromDate && (
                  <button onClick={() => setFromDate("")} data-testid="purge-from-clear" className="mono text-[10px] uppercase tracking-widest px-2 py-2 border border-industrial hover:border-red-500 hover:text-red-400" title="Since forever">✕</button>
                )}
              </div>
              <div className="mono text-[10px] text-muted-industrial mt-1">
                {fromDate ? <>Include dates <b className="text-fuchsia-300">≥ {fromDate}</b></> : <span className="text-amber-400">Since forever — every record on or before the To date</span>}
              </div>
            </div>
            <div>
              <div className="label-tag mb-1">To (inclusive · required)</div>
              <div className="flex items-center gap-1">
                <button onClick={() => shift(setDate, date, -1)} data-testid="purge-to-prev" className="mono text-xs uppercase tracking-widest px-2 py-2 border border-industrial hover:border-fuchsia-500 hover:text-fuchsia-400">←</button>
                <Input
                  data-testid="purge-date-input"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="rounded-none bg-transparent border-industrial mono flex-1"
                />
                <button onClick={() => shift(setDate, date, 1)} data-testid="purge-to-next" className="mono text-xs uppercase tracking-widest px-2 py-2 border border-industrial hover:border-fuchsia-500 hover:text-fuchsia-400">→</button>
              </div>
              <div className="mono text-[10px] text-muted-industrial mt-1">
                Include dates <b className="text-fuchsia-300">≤ {date}</b>
              </div>
            </div>
          </div>

          {rangeInvalid && (
            <div className="border border-red-600 bg-red-950/20 p-2 text-[11px] mono text-red-300" data-testid="purge-range-error">
              From date is later than To date — please fix the range.
            </div>
          )}

          {/* Kind selector grid */}
          <div>
            <div className="label-tag mb-1">What to archive</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-1" data-testid="purge-kinds-grid">
              {PURGE_KINDS.map((k) => {
                const sc = scopeOf(k.key);
                const on = !!enabled[k.key];
                return (
                  <label
                    key={k.key}
                    data-testid={`purge-kind-${k.key}`}
                    className={`border p-2 cursor-pointer flex flex-col gap-0.5 ${on ? "border-fuchsia-500 bg-fuchsia-950/10" : "border-industrial opacity-60 hover:opacity-100"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="mono text-[10px] uppercase tracking-widest">{k.label}</span>
                      <Checkbox
                        checked={on}
                        onCheckedChange={(v) => setEnabled({ ...enabled, [k.key]: !!v })}
                        data-testid={`purge-kind-cb-${k.key}`}
                      />
                    </div>
                    <div className={`mono text-lg font-bold ${sc && sc.count ? "text-fuchsia-300" : "text-muted-industrial"}`}>
                      {loading ? "…" : (sc?.count ?? 0)}
                    </div>
                    {k.key === "invoice" && sc && sc.value > 0 && (
                      <div className="mono text-[10px] text-muted-industrial">${sc.value.toFixed(2)}</div>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Grand total */}
          {preview && !result && (
            <div className="border border-fuchsia-700 bg-fuchsia-950/10 p-3 flex items-center justify-between" data-testid="purge-total-bar">
              <div>
                <div className="label-tag text-fuchsia-400">Grand Total</div>
                <div className="mono text-2xl font-black text-fuchsia-300">{preview.total} rows</div>
              </div>
              <div className="text-right">
                <div className="label-tag">Range</div>
                <div className="mono text-[11px] text-ink"><b className="text-fuchsia-300">{fromDate || "∞"}</b> → <b className="text-fuchsia-300">{date}</b></div>
              </div>
            </div>
          )}

          {/* Confirmation */}
          {preview && !result && preview.total > 0 && !rangeInvalid && (
            <div className="border border-fuchsia-700 bg-fuchsia-950/10 p-3">
              <div className="label-tag text-fuchsia-400 mb-2">Type this to confirm</div>
              <div className="flex items-center gap-2">
                <span className="mono text-xs bg-surface-2 border border-industrial px-2 py-1.5 text-fuchsia-300 select-all">{expectedConfirm}</span>
                <Input
                  data-testid="purge-confirm-input"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={`Type ${expectedConfirm}`}
                  className="rounded-none bg-transparent border-industrial mono flex-1"
                />
              </div>
            </div>
          )}

          {/* Nothing to purge */}
          {preview && !result && preview.total === 0 && !rangeInvalid && (
            <div className="border border-industrial p-4 text-center text-muted-industrial mono text-xs uppercase" data-testid="purge-empty">
              Nothing dated between {fromDate || "∞"} and {date} (for the selected kinds).
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="border-2 border-emerald-600 bg-emerald-950/20 p-4 space-y-2" data-testid="purge-result">
              <div className="label-tag text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Purge complete</div>
              <div className="mono text-xs text-ink">
                Archived <b className="text-emerald-300">{result.total_moved}</b> rows{result.total_value_moved > 0 ? <> · <b className="text-emerald-300">${result.total_value_moved.toFixed(2)}</b> of trial invoicing removed</> : ""}.
              </div>
              <div className="mono text-[10px] text-muted-industrial space-y-0.5">
                {Object.entries(result.counts || {}).map(([coll, n]) => (
                  <div key={coll}><span className="text-emerald-300">{n}</span> × {coll}</div>
                ))}
              </div>
              <div className="text-[11px] text-muted-industrial pt-2 border-t border-industrial">
                Audit batch id: <span className="mono text-fuchsia-300 select-all">{result.batch_id}</span>. Restore any day via Recall Day → Restore Archived.
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {!result && (
            <Button
              onClick={run}
              disabled={!canRun}
              data-testid="purge-execute-btn"
              className="rounded-none bg-fuchsia-600 hover:bg-fuchsia-500 text-white uppercase tracking-widest text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4 mr-1" />{running ? "Archiving…" : `Archive ${preview?.total || 0} Rows`}
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)} data-testid="purge-close-btn" className="rounded-none bg-surface-2 border border-industrial hover:border-primary-orange uppercase tracking-widest text-xs">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



/* -----------------------------------------------------------
 * v3.9 · Reset & Rebuild — one-click chained action:
 *   1. Void every invoice covering the day's dockets (docket_day match)
 *   2. Re-run the daily invoice run to rebuild them from current data
 * --------------------------------------------------------- */
function ResetAndRebuildDialog({ open, onOpenChange, initialDate, onDone }) {
  const [mode, setMode] = useState("single");   // v3.9 · "single" | "range"
  const [date, setDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [voidPreview, setVoidPreview] = useState(null);
  const [rebuildPreview, setRebuildPreview] = useState(null);
  const [rangePreview, setRangePreview] = useState(null);  // {days:[{date,voidPrev,rebuildPrev}], totals:{...}}
  const [loading, setLoading] = useState(false);
  const [includePaid, setIncludePaid] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState("idle");
  const [progress, setProgress] = useState({ current: 0, total: 0, currentDate: "" });
  const [result, setResult] = useState(null);

  const defaultDate = () => {
    const y = new Date(); y.setDate(y.getDate() - 1);
    const yy = y.getFullYear(); const mm = String(y.getMonth() + 1).padStart(2, "0"); const dd = String(y.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  };

  useEffect(() => {
    if (open) {
      const d = initialDate || defaultDate();
      setDate(d);
      // Default range = last 7 days ending yesterday
      const t = new Date(d + "T00:00:00");
      const from = new Date(t); from.setDate(from.getDate() - 6);
      const fmt = (dt) => `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
      setFromDate(fmt(from));
      setToDate(d);
      setMode(initialDate ? "single" : "single");
      setVoidPreview(null); setRebuildPreview(null); setRangePreview(null);
      setConfirmText(""); setIncludePaid(false);
      setStep("idle"); setResult(null);
      setProgress({ current: 0, total: 0, currentDate: "" });
    }
  }, [open, initialDate]);

  const enumerateDates = (from, to) => {
    if (!from || !to || from > to) return [];
    const out = [];
    const d = new Date(from + "T00:00:00");
    const end = new Date(to + "T00:00:00");
    while (d <= end) {
      out.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`);
      d.setDate(d.getDate() + 1);
    }
    return out;
  };

  const loadPreview = async () => {
    setLoading(true); setVoidPreview(null); setRebuildPreview(null); setRangePreview(null);
    try {
      if (mode === "single") {
        const [voidR, rebR] = await Promise.all([
          api.post("/invoices/void-day", { date, confirm: "preview", dry_run: true, include_paid: includePaid, match: "docket_day" }),
          api.get(`/invoices/daily-preview?date=${date}`),
        ]);
        setVoidPreview(voidR.data);
        setRebuildPreview(rebR.data);
      } else {
        const dates = enumerateDates(fromDate, toDate);
        if (dates.length === 0) { setRangePreview({ days: [], totals: {} }); return; }
        const days = [];
        let tVoid = 0, tReleased = 0, tMoney = 0, tCreate = 0, tValue = 0;
        // Sequential (not parallel) — most preview endpoints are cheap
        for (const d of dates) {
          try {
            const [voidR, rebR] = await Promise.all([
              api.post("/invoices/void-day", { date: d, confirm: "preview", dry_run: true, include_paid: includePaid, match: "docket_day" }),
              api.get(`/invoices/daily-preview?date=${d}`),
            ]);
            days.push({ date: d, voidPrev: voidR.data, rebuildPrev: rebR.data });
            tVoid += voidR.data.voided_count || 0;
            tReleased += voidR.data.dockets_released || 0;
            tMoney += voidR.data.money_voided || 0;
            tCreate += rebR.data.total_invoices || 0;
            tValue += rebR.data.total_value || 0;
          } catch (e) {
            days.push({ date: d, error: e?.response?.data?.detail || "preview failed" });
          }
        }
        setRangePreview({
          days,
          totals: { voided: tVoid, released: tReleased, money: tMoney, creating: tCreate, value: tValue },
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    if (mode === "single" && date) loadPreview();
    if (mode === "range" && fromDate && toDate) loadPreview();
    /* eslint-disable-next-line */
  }, [open, mode, date, fromDate, toDate, includePaid]);

  const expectedConfirm = mode === "range" ? `RESET-${fromDate}_TO_${toDate}` : `RESET-${date}`;
  const hasWorkSingle = voidPreview && (voidPreview.voided_count > 0 || (rebuildPreview?.total_invoices || 0) > 0);
  const hasWorkRange = rangePreview && (rangePreview.totals.voided > 0 || rangePreview.totals.creating > 0);
  const hasWorkToDo = mode === "range" ? hasWorkRange : hasWorkSingle;
  const canRun = hasWorkToDo && confirmText === expectedConfirm && !running;

  const executeOneDay = async (targetDate) => {
    // Void + collect released_docket_dates, then rebuild for each affected date.
    let voidRes = { voided_count: 0, dockets_released: 0, money_voided: 0, released_docket_dates: [] };
    // We use dry_run=false blindly — the caller already got user confirmation.
    try {
      const r = await api.post("/invoices/void-day", {
        date: targetDate,
        confirm: `VOID-${targetDate}`,
        dry_run: false,
        include_paid: includePaid,
        match: "docket_day",
      });
      voidRes = r.data;
    } catch (e) {
      // If voided_count was 0, void endpoint returns 200; only actual errors land here
      return { date: targetDate, voidRes, rebuilds: [], error: e?.response?.data?.detail || "void failed" };
    }
    // Rebuild for every affected plant-local date (self + released_docket_dates)
    const rebuildDates = new Set([targetDate, ...(voidRes.released_docket_dates || [])]);
    const rebuilds = [];
    for (const d of rebuildDates) {
      try {
        const prev = await api.get(`/invoices/daily-preview?date=${d}`);
        const gks = (prev.data.groups || []).map((g) => g.group_key);
        if (gks.length === 0) {
          rebuilds.push({ date: d, created: [], total_value: 0, empty: true });
          continue;
        }
        const rr = await api.post("/invoices/daily-run", { date: d, group_keys: gks });
        rebuilds.push({ date: d, ...rr.data });
      } catch (e) {
        rebuilds.push({ date: d, error: e?.response?.data?.detail || "rebuild failed", created: [], total_value: 0 });
      }
    }
    return { date: targetDate, voidRes, rebuilds };
  };

  const run = async () => {
    if (!canRun) return;
    setRunning(true); setResult(null);
    try {
      const targets = mode === "range" ? enumerateDates(fromDate, toDate) : [date];
      setProgress({ current: 0, total: targets.length, currentDate: "" });
      const dayResults = [];
      const allRebuilds = new Map();   // dedupe by date across the whole run
      let totalVoided = 0, totalReleased = 0, totalMoney = 0;
      for (let i = 0; i < targets.length; i++) {
        const d = targets[i];
        setProgress({ current: i + 1, total: targets.length, currentDate: d });
        setStep("voiding");
        const one = await executeOneDay(d);
        dayResults.push(one);
        totalVoided += one.voidRes?.voided_count || 0;
        totalReleased += one.voidRes?.dockets_released || 0;
        totalMoney += one.voidRes?.money_voided || 0;
        for (const rb of (one.rebuilds || [])) {
          // Later rebuilds for the same date OVERWRITE earlier ones (they see fresh state)
          allRebuilds.set(rb.date, rb);
        }
      }
      const perDate = Array.from(allRebuilds.values()).sort((a,b) => a.date.localeCompare(b.date));
      const created = perDate.flatMap((r) => r.created || []);
      const totalValue = perDate.reduce((s, r) => s + (r.total_value || 0), 0);
      setResult({
        mode,
        targets,
        dayResults,
        voidRes: { voided_count: totalVoided, dockets_released: totalReleased, money_voided: totalMoney, released_docket_dates: perDate.map((r) => r.date) },
        rebuildRes: { created, total_value: totalValue, per_date: perDate },
      });
      setStep("done");
      toast.success(`Voided ${totalVoided} · created ${created.length} across ${perDate.length} day${perDate.length === 1 ? "" : "s"} · $${totalValue.toFixed(2)}`, { duration: 12000 });
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset & Rebuild failed");
      setStep("idle");
    } finally { setRunning(false); }
  };

  const shift = (setter, base, days) => {
    if (!base) return;
    const d = new Date(base + "T00:00:00"); d.setDate(d.getDate() + days);
    const yy = d.getFullYear(); const mm = String(d.getMonth() + 1).padStart(2, "0"); const dd = String(d.getDate()).padStart(2, "0");
    setter(`${yy}-${mm}-${dd}`);
  };

  const eligibleForRebuild = rebuildPreview?.total_invoices || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-surface border-2 border-emerald-600 max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="rebuild-day-dialog">
        <DialogHeader>
          <DialogTitle className="heading uppercase inline-flex items-center gap-2 text-emerald-400">
            <RefreshCcw className="w-4 h-4" /> Reset &amp; Rebuild Day
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="border-l-4 border-emerald-600 bg-emerald-950/20 pl-3 py-2 text-[11px] mono text-ink">
            <div className="uppercase tracking-widest text-emerald-400 font-bold text-[10px] mb-1">Void &amp; re-issue in one shot</div>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <div><b className="text-red-300">Step 1</b> · void every invoice covering a docket loaded on the target day(s) — releases dockets back to "uninvoiced"</div>
              <div><b className="text-emerald-300">Step 2</b> · immediately re-run the Daily Invoice Run for each affected day to rebuild fresh invoices from the current docket data</div>
            </div>
            <div className="text-[10px] text-muted-industrial mt-2">Perfect after fixing wait-time, extras, cartage or customer errors. Paid invoices are protected unless you tick include-paid.</div>
          </div>

          {/* v3.9 · Mode toggle: single day vs range */}
          <div className="grid grid-cols-2 gap-1" data-testid="rebuild-mode-toggle">
            <button
              type="button"
              onClick={() => setMode("single")}
              data-testid="rebuild-mode-single"
              className={`p-2 border text-left ${mode === "single" ? "border-emerald-500 bg-emerald-950/20 text-emerald-300" : "border-industrial text-muted-industrial hover:text-ink"}`}
            >
              <div className="mono text-[10px] uppercase tracking-widest font-bold">Single Day</div>
              <div className="mono text-[9px] opacity-70 mt-0.5">Reset & rebuild one plant-local day.</div>
            </button>
            <button
              type="button"
              onClick={() => setMode("range")}
              data-testid="rebuild-mode-range"
              className={`p-2 border text-left ${mode === "range" ? "border-emerald-500 bg-emerald-950/20 text-emerald-300" : "border-industrial text-muted-industrial hover:text-ink"}`}
            >
              <div className="mono text-[10px] uppercase tracking-widest font-bold">Date Range</div>
              <div className="mono text-[9px] opacity-70 mt-0.5">Void &amp; rebuild every day in the window — perfect for a whole week's cleanup.</div>
            </button>
          </div>

          {/* Date pickers */}
          {mode === "single" ? (
            <div className="flex items-center gap-2">
              <button onClick={() => shift(setDate, date, -1)} data-testid="rebuild-prev-day" className="mono text-xs uppercase tracking-widest px-3 py-2 border border-industrial hover:border-emerald-500 hover:text-emerald-400">← Prev</button>
              <Input
                data-testid="rebuild-date-input"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="rounded-none bg-transparent border-industrial mono flex-1"
              />
              <button onClick={() => shift(setDate, date, 1)} data-testid="rebuild-next-day" className="mono text-xs uppercase tracking-widest px-3 py-2 border border-industrial hover:border-emerald-500 hover:text-emerald-400">Next →</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="label-tag mb-1">From (inclusive)</div>
                <div className="flex items-center gap-1">
                  <button onClick={() => shift(setFromDate, fromDate, -1)} data-testid="rebuild-from-prev" className="mono text-xs uppercase tracking-widest px-2 py-2 border border-industrial hover:border-emerald-500 hover:text-emerald-400">←</button>
                  <Input data-testid="rebuild-from-input" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="rounded-none bg-transparent border-industrial mono flex-1" />
                  <button onClick={() => shift(setFromDate, fromDate, 1)} data-testid="rebuild-from-next" className="mono text-xs uppercase tracking-widest px-2 py-2 border border-industrial hover:border-emerald-500 hover:text-emerald-400">→</button>
                </div>
              </div>
              <div>
                <div className="label-tag mb-1">To (inclusive)</div>
                <div className="flex items-center gap-1">
                  <button onClick={() => shift(setToDate, toDate, -1)} data-testid="rebuild-to-prev" className="mono text-xs uppercase tracking-widest px-2 py-2 border border-industrial hover:border-emerald-500 hover:text-emerald-400">←</button>
                  <Input data-testid="rebuild-to-input" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="rounded-none bg-transparent border-industrial mono flex-1" />
                  <button onClick={() => shift(setToDate, toDate, 1)} data-testid="rebuild-to-next" className="mono text-xs uppercase tracking-widest px-2 py-2 border border-industrial hover:border-emerald-500 hover:text-emerald-400">→</button>
                </div>
              </div>
              {fromDate && toDate && fromDate > toDate && (
                <div className="col-span-2 border border-red-600 bg-red-950/20 p-2 mono text-[11px] text-red-300" data-testid="rebuild-range-error">From date is later than To date — please fix the range.</div>
              )}
              {fromDate && toDate && fromDate <= toDate && (
                <div className="col-span-2 mono text-[10px] text-muted-industrial">
                  Range covers <b className="text-emerald-300">{enumerateDates(fromDate, toDate).length}</b> day{enumerateDates(fromDate, toDate).length === 1 ? "" : "s"} — each processed sequentially.
                </div>
              )}
            </div>
          )}

          {/* Include paid toggle */}
          <label className="flex items-center gap-2 text-[11px] mono uppercase tracking-widest cursor-pointer" data-testid="rebuild-include-paid-label">
            <Checkbox checked={includePaid} onCheckedChange={setIncludePaid} data-testid="rebuild-include-paid-cb" />
            <span className={includePaid ? "text-amber-400" : "text-muted-industrial"}>Include paid invoices (refund first!)</span>
          </label>

          {loading && <div className="text-center py-4 text-muted-industrial mono text-xs uppercase" data-testid="rebuild-loading">Loading preview…</div>}

          {/* v3.9 · Range preview */}
          {mode === "range" && rangePreview && !result && (
            <>
              <div className="grid grid-cols-2 gap-3" data-testid="rebuild-range-summary">
                <div className="border border-red-700 bg-red-950/10 p-3 space-y-2">
                  <div className="label-tag text-red-400 flex items-center gap-1"><Undo2 className="w-3 h-3" />Step 1 · Void</div>
                  <div className="mono text-4xl font-black text-red-300" data-testid="rebuild-range-void-count">{rangePreview.totals.voided}</div>
                  <div className="mono text-[11px] text-ink">invoice{rangePreview.totals.voided === 1 ? "" : "s"} to void</div>
                  <div className="mono text-[10px] text-muted-industrial space-y-0.5">
                    <div><span className="text-red-300">{rangePreview.totals.released}</span> dockets released</div>
                    <div><span className="text-red-300">${(rangePreview.totals.money || 0).toFixed(2)}</span> ex-tax freed</div>
                  </div>
                </div>
                <div className="border border-emerald-700 bg-emerald-950/10 p-3 space-y-2">
                  <div className="label-tag text-emerald-400 flex items-center gap-1"><Zap className="w-3 h-3" />Step 2 · Rebuild</div>
                  <div className="mono text-4xl font-black text-emerald-300" data-testid="rebuild-range-create-count">{rangePreview.totals.creating}<span className="text-[11px] text-muted-industrial">*</span></div>
                  <div className="mono text-[11px] text-ink">invoice{rangePreview.totals.creating === 1 ? "" : "s"} to create</div>
                  <div className="mono text-[10px] text-muted-industrial space-y-0.5">
                    <div><span className="text-emerald-300">${(rangePreview.totals.value || 0).toFixed(2)}</span> new billing (est. ex-GST)</div>
                    <div className="text-[9px] opacity-70">* Recounts per day after each void — final # includes released dockets</div>
                  </div>
                </div>
              </div>

              {/* Per-day breakdown table */}
              {rangePreview.days.length > 0 && (
                <div className="max-h-[240px] overflow-y-auto border border-industrial">
                  <table className="w-full text-[11px] mono">
                    <thead className="sticky top-0 bg-surface-2 border-b border-industrial">
                      <tr className="text-left">
                        <th className="p-2 label-tag">Date</th>
                        <th className="p-2 label-tag text-right">Void</th>
                        <th className="p-2 label-tag text-right">Released</th>
                        <th className="p-2 label-tag text-right">Free $</th>
                        <th className="p-2 label-tag text-right">Rebuild</th>
                        <th className="p-2 label-tag text-right">New $</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rangePreview.days.map((d) => (
                        <tr key={d.date} className="border-b border-industrial/40" data-testid={`rebuild-range-row-${d.date}`}>
                          <td className="p-2 text-ink">{d.date}</td>
                          {d.error ? (
                            <td colSpan="5" className="p-2 text-red-400 text-right">{d.error}</td>
                          ) : (
                            <>
                              <td className={`p-2 text-right ${d.voidPrev?.voided_count ? "text-red-300" : "text-muted-industrial"}`}>{d.voidPrev?.voided_count || 0}</td>
                              <td className="p-2 text-right text-muted-industrial">{d.voidPrev?.dockets_released || 0}</td>
                              <td className="p-2 text-right text-muted-industrial">${(d.voidPrev?.money_voided || 0).toFixed(2)}</td>
                              <td className={`p-2 text-right ${d.rebuildPrev?.total_invoices ? "text-emerald-300" : "text-muted-industrial"}`}>{d.rebuildPrev?.total_invoices || 0}</td>
                              <td className="p-2 text-right text-primary-orange">${(d.rebuildPrev?.total_value || 0).toFixed(2)}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!hasWorkRange && (
                <div className="border border-industrial p-4 text-center text-muted-industrial mono text-xs uppercase" data-testid="rebuild-range-empty">
                  Nothing to reset or rebuild between {fromDate} and {toDate}.
                </div>
              )}

              {hasWorkRange && (
                <div className="border border-emerald-700 bg-emerald-950/10 p-3">
                  <div className="label-tag text-emerald-400 mb-2">Type this to confirm</div>
                  <div className="flex items-center gap-2">
                    <span className="mono text-xs bg-surface-2 border border-industrial px-2 py-1.5 text-emerald-300 select-all">{expectedConfirm}</span>
                    <Input
                      data-testid="rebuild-confirm-input"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder={`Type ${expectedConfirm}`}
                      className="rounded-none bg-transparent border-industrial mono flex-1"
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {mode === "single" && voidPreview && rebuildPreview && !result && (
            <>
              <div className="grid grid-cols-2 gap-3" data-testid="rebuild-summary">
                <div className="border border-red-700 bg-red-950/10 p-3 space-y-2">
                  <div className="label-tag text-red-400 flex items-center gap-1"><Undo2 className="w-3 h-3" />Step 1 · Void</div>
                  <div className="mono text-4xl font-black text-red-300" data-testid="rebuild-void-count">{voidPreview.voided_count}</div>
                  <div className="mono text-[11px] text-ink">invoice{voidPreview.voided_count === 1 ? "" : "s"} to void</div>
                  <div className="mono text-[10px] text-muted-industrial space-y-0.5">
                    <div><span className="text-red-300">{voidPreview.dockets_released}</span> dockets released</div>
                    <div><span className="text-red-300">${(voidPreview.money_voided || 0).toFixed(2)}</span> ex-tax freed</div>
                    {voidPreview.skipped_count > 0 && <div className="text-amber-400">{voidPreview.skipped_count} skipped (paid / voided)</div>}
                  </div>
                </div>
                <div className="border border-emerald-700 bg-emerald-950/10 p-3 space-y-2">
                  <div className="label-tag text-emerald-400 flex items-center gap-1"><Zap className="w-3 h-3" />Step 2 · Rebuild</div>
                  <div className="mono text-4xl font-black text-emerald-300" data-testid="rebuild-create-count">
                    {eligibleForRebuild}<span className="text-[11px] text-muted-industrial">*</span>
                  </div>
                  <div className="mono text-[11px] text-ink">invoice{eligibleForRebuild === 1 ? "" : "s"} to create</div>
                  <div className="mono text-[10px] text-muted-industrial space-y-0.5">
                    <div><span className="text-emerald-300">{(rebuildPreview.total_m3 || 0).toFixed(1)}</span> m³ across <span className="text-emerald-300">{rebuildPreview.customers?.length || 0}</span> customer{(rebuildPreview.customers?.length || 0) === 1 ? "" : "s"}</div>
                    <div><span className="text-emerald-300">${(rebuildPreview.total_value || 0).toFixed(2)}</span> new billing (est. ex-GST)</div>
                    <div className="text-[9px] opacity-70">* Recounts after void — final # includes the released dockets</div>
                  </div>
                </div>
              </div>

              {/* v3.9 · Multi-day rebuild warning */}
              {(voidPreview.released_docket_dates || []).length > 0 && (
                (voidPreview.released_docket_dates.length > 1 || voidPreview.released_docket_dates[0] !== date) && (
                  <div className="border-2 border-amber-600 bg-amber-950/20 p-3" data-testid="rebuild-multi-day-warn">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                      <div className="text-[11px] mono text-ink flex-1">
                        <div className="uppercase tracking-widest text-amber-400 font-bold text-[10px] mb-1">Multi-day rebuild</div>
                        The voided invoice{voidPreview.voided_count === 1 ? "" : "s"} covers dockets loaded on{" "}
                        {voidPreview.released_docket_dates.map((d, i) => (
                          <span key={d}>
                            <b className="text-amber-300">{d}</b>{i < voidPreview.released_docket_dates.length - 1 ? ", " : ""}
                          </span>
                        ))}.
                        <br />
                        Reset &amp; Rebuild will automatically re-run the Daily Invoice Run for <b className="text-amber-300">each of those days</b> so
                        every released docket gets re-invoiced — not just {date}.
                      </div>
                    </div>
                  </div>
                )
              )}

              {voidPreview.voided_count === 0 && eligibleForRebuild === 0 && (
                <div className="border border-industrial p-4 text-center text-muted-industrial mono text-xs uppercase" data-testid="rebuild-nothing">
                  Nothing to reset or rebuild for {date}.
                </div>
              )}

              {hasWorkToDo && (
                <div className="border border-emerald-700 bg-emerald-950/10 p-3">
                  <div className="label-tag text-emerald-400 mb-2">Type this to confirm</div>
                  <div className="flex items-center gap-2">
                    <span className="mono text-xs bg-surface-2 border border-industrial px-2 py-1.5 text-emerald-300 select-all">{expectedConfirm}</span>
                    <Input
                      data-testid="rebuild-confirm-input"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder={`Type ${expectedConfirm}`}
                      className="rounded-none bg-transparent border-industrial mono flex-1"
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {running && (
            <div className="border-2 border-emerald-600 bg-emerald-950/20 p-4 space-y-2" data-testid="rebuild-running">
              <div className="flex items-center gap-2 mono text-xs uppercase tracking-widest">
                {step === "voiding" && <><Undo2 className="w-4 h-4 text-red-400 animate-pulse" /><span className="text-red-300">Voiding invoices…</span></>}
                {step === "rebuilding" && <><Zap className="w-4 h-4 text-emerald-400 animate-pulse" /><span className="text-emerald-300">Rebuilding invoices…</span></>}
                {progress.total > 1 && (
                  <span className="ml-auto text-muted-industrial text-[10px]">
                    Day <b className="text-emerald-300">{progress.current}</b> / {progress.total} · <b className="text-emerald-300">{progress.currentDate}</b>
                  </span>
                )}
              </div>
              {progress.total > 1 && (
                <div className="w-full h-1 bg-industrial/30">
                  <div className="h-full bg-emerald-500 transition-all" style={{width: `${(progress.current / progress.total) * 100}%`}} />
                </div>
              )}
            </div>
          )}

          {result && (
            <div className="border-2 border-emerald-600 bg-emerald-950/20 p-4 space-y-2" data-testid="rebuild-result">
              <div className="label-tag text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Reset &amp; Rebuild complete</div>
              <div className="grid grid-cols-2 gap-3 mono text-xs">
                <div>
                  <div className="text-red-400 uppercase tracking-widest text-[10px] mb-1">Voided</div>
                  <div className="text-ink">
                    <b className="text-red-300">{result.voidRes.voided_count}</b> invoices ·
                    <b className="text-red-300"> {result.voidRes.dockets_released}</b> dockets released ·
                    <b className="text-red-300"> ${(result.voidRes.money_voided || 0).toFixed(2)}</b>
                  </div>
                </div>
                <div>
                  <div className="text-emerald-400 uppercase tracking-widest text-[10px] mb-1">Rebuilt</div>
                  <div className="text-ink">
                    <b className="text-emerald-300">{(result.rebuildRes.created || []).length}</b> new invoices ·
                    <b className="text-emerald-300"> ${(result.rebuildRes.total_value || 0).toFixed(2)}</b> billed
                  </div>
                </div>
              </div>

              {/* v3.9 · Post-rebuild MYOB export — exports EXACTLY the new invoices */}
              {(result.rebuildRes.created || []).length > 0 && (
                <div className="border border-primary-orange bg-primary-orange/10 p-3 flex items-center justify-between gap-2 flex-wrap" data-testid="rebuild-export-section">
                  <div className="flex-1 min-w-[220px]">
                    <div className="label-tag text-primary-orange">Send to MYOB</div>
                    <div className="mono text-[11px] text-ink">Grab a MYOB Pro TXT with just these <b className="text-primary-orange">{(result.rebuildRes.created || []).length}</b> newly-issued invoices — bypasses the "since last export" filter.</div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <a
                      href={apiHref(`/invoices/export/myob-professional.txt?ids=${(result.rebuildRes.created || []).map((c) => c.invoice_id).join(",")}`)}
                      target="_blank" rel="noreferrer"
                      data-testid="rebuild-export-myob-pro-btn"
                      className="border-2 rounded-none px-3 py-2 uppercase tracking-widest text-xs font-bold inline-flex items-center gap-1.5 no-underline border-primary-orange bg-primary-orange text-black hover:bg-[rgb(var(--primary-hover))]"
                    >
                      <Download className="w-4 h-4" />MYOB Pro · TXT
                    </a>
                    <a
                      href={apiHref(`/invoices/export/xero.csv?ids=${(result.rebuildRes.created || []).map((c) => c.invoice_id).join(",")}`)}
                      target="_blank" rel="noreferrer"
                      data-testid="rebuild-export-xero-btn"
                      className="border-2 rounded-none px-3 py-2 uppercase tracking-widest text-xs font-bold inline-flex items-center gap-1.5 no-underline border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange"
                    >
                      <Download className="w-4 h-4" />Xero · CSV
                    </a>
                  </div>
                </div>
              )}

              {/* v3.9 · Per-date breakdown when multi-day + per-day export */}
              {(result.rebuildRes.per_date || []).length > 1 && (
                <div className="border border-emerald-700 bg-emerald-950/5 p-2" data-testid="rebuild-per-date">
                  <div className="label-tag text-emerald-400 mb-1">Per-day rebuild &amp; export</div>
                  <div className="mono text-[10px] space-y-0.5">
                    {result.rebuildRes.per_date.map((r, i) => {
                      const dayIds = (r.created || []).map((c) => c.invoice_id).join(",");
                      return (
                        <div key={i} className="flex items-center justify-between border-b border-industrial/40 py-1 gap-2">
                          <span className="text-ink shrink-0">{r.date}</span>
                          <span className="text-muted-industrial flex-1 text-center">
                            {(r.created || []).length} invoice{(r.created || []).length === 1 ? "" : "s"} · ${(r.total_value || 0).toFixed(2)}
                            {r.empty && <span className="text-amber-400"> (no eligible dockets)</span>}
                            {r.error && <span className="text-red-400"> · {r.error}</span>}
                          </span>
                          {dayIds && (
                            <a
                              href={apiHref(`/invoices/export/myob-professional.txt?ids=${dayIds}`)}
                              target="_blank" rel="noreferrer"
                              data-testid={`rebuild-export-day-${r.date}`}
                              className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-primary-orange text-primary-orange hover:bg-primary-orange hover:text-black shrink-0"
                              title="Download MYOB TXT for just this day"
                            >
                              <Download className="w-3 h-3 inline mr-0.5" />MYOB
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {(result.rebuildRes.created || []).length > 0 && (
                <div className="max-h-[200px] overflow-y-auto border border-industrial mt-2">
                  <table className="w-full text-[11px] mono">
                    <thead className="sticky top-0 bg-surface-2 border-b border-industrial">
                      <tr className="text-left">
                        <th className="p-2 label-tag">New Invoice</th>
                        <th className="p-2 label-tag">Customer</th>
                        <th className="p-2 label-tag">Site / PO</th>
                        <th className="p-2 label-tag text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.rebuildRes.created.map((c, idx) => (
                        <tr key={idx} className="border-b border-industrial/40" data-testid={`rebuild-created-${c.invoice_no}`}>
                          <td className="p-2 text-emerald-300">{c.invoice_no}</td>
                          <td className="p-2 text-ink">{c.customer_name}</td>
                          <td className="p-2 opacity-70 text-[10px]">{c.site_name || ""}{c.po_number ? ` · PO ${c.po_number}` : ""}</td>
                          <td className="p-2 text-right text-primary-orange">${(c.total || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {result.voidRes.voided_count > 0 && (result.rebuildRes.created || []).length === 0 && (
                <div className="border-2 border-amber-600 bg-amber-950/20 p-3 mt-2" data-testid="rebuild-empty-explain">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                    <div className="text-[11px] mono text-ink flex-1">
                      <div className="uppercase tracking-widest text-amber-400 font-bold text-[10px] mb-1">Voided, but nothing to rebuild yet</div>
                      Every released docket was either <b>not delivered</b>, has <b>no customer</b>, or belongs to a
                      <b> EFT-sale</b> customer (which never gets a daily-run invoice). Check the docket statuses
                      via Recall Day for the affected dates
                      {result.voidRes.released_docket_dates?.length ? <> — <b className="text-amber-300">{result.voidRes.released_docket_dates.join(", ")}</b></> : ""}.
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {!result && (
            <Button
              onClick={run}
              disabled={!canRun}
              data-testid="rebuild-execute-btn"
              className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-black uppercase tracking-widest text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RefreshCcw className={`w-4 h-4 mr-1 ${running ? "animate-spin" : ""}`} />
              {running ? "Working…" : (
                mode === "range"
                  ? `Reset & Rebuild ${rangePreview?.totals?.voided || 0} → ${rangePreview?.totals?.creating || 0} across ${enumerateDates(fromDate, toDate).length} days`
                  : `Reset & Rebuild ${voidPreview?.voided_count || 0} → ${eligibleForRebuild}`
              )}
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)} data-testid="rebuild-close-btn" className="rounded-none bg-surface-2 border border-industrial hover:border-primary-orange uppercase tracking-widest text-xs">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}




// ─── EDIT INVOICE DIALOG ────────────────────────────────────────────
// v3.11 · Full line-item editor for correcting an issued invoice — used
// for credit notes, price corrections, and re-sending a fixed invoice
// to the customer. Blocks edits on paid/voided invoices via the
// backend guard. Every save is stamped in the invoice's `edit_history[]`
// audit trail.
function EditInvoiceDialog({ invoice, onClose, onSaved }) {
  const [lines, setLines] = useState(() =>
    (invoice.lines || []).map((l) => ({
      docket_no: l.docket_no || "—",
      date: l.date || "",
      mix_code: l.mix_code || "",
      quantity_m3: l.quantity_m3 || 0,
      price_per_m3: l.price_per_m3 || 0,
      amount: l.amount || 0,
    }))
  );
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState(invoice.notes || "");
  const [isCredit, setIsCredit] = useState(!!invoice.is_credit_note);
  const [saving, setSaving] = useState(false);
  const [alsoResend, setAlsoResend] = useState(true);

  // Live totals preview
  const subtotal = lines.reduce((s, l) => {
    const qty = Number(l.quantity_m3) || 0;
    const rate = Number(l.price_per_m3) || 0;
    if (qty === 0 && rate === 0) return s + (Number(l.amount) || 0);
    return s + qty * rate;
  }, 0);
  const taxRate = invoice.tax_rate || 0.10;
  const tax = subtotal * taxRate;
  const total = subtotal + tax;

  const patchLine = (idx, patch) => {
    setLines((prev) => {
      const next = [...prev];
      const merged = { ...next[idx], ...patch };
      const qty = Number(merged.quantity_m3) || 0;
      const rate = Number(merged.price_per_m3) || 0;
      if (qty !== 0 || rate !== 0) merged.amount = Number((qty * rate).toFixed(2));
      next[idx] = merged;
      return next;
    });
  };
  const addLine = () => setLines((prev) => [...prev, { docket_no: "—", date: "", mix_code: "", quantity_m3: 0, price_per_m3: 0, amount: 0 }]);
  const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    if (reason.trim().length < 3) {
      toast.error("Enter a short reason for the edit (kept in audit log)");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/invoices/${invoice.id}`, {
        lines,
        notes,
        edit_reason: reason.trim(),
        recompute_amounts: true,
        is_credit_note: isCredit,
      });
      toast.success(`Invoice ${data.invoice_no} saved · new total $${data.total.toFixed(2)}`);
      await onSaved?.(data, alsoResend);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="rounded-none bg-surface border-industrial max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="edit-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="heading uppercase">
            Edit Invoice <span className="text-primary-orange">{invoice.invoice_no}</span> · {invoice.customer_name}
          </DialogTitle>
        </DialogHeader>

        {invoice.status !== "unpaid" && (
          <div className="border border-amber-500 bg-amber-950/20 p-2.5 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <div className="mono text-[11px] text-amber-200">
              This invoice is <b>{invoice.status.toUpperCase()}</b>. Edits are blocked server-side for paid/voided invoices — refund or re-issue first.
            </div>
          </div>
        )}

        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          <div className="grid grid-cols-12 gap-2 label-tag pb-1 border-b border-industrial">
            <div className="col-span-1">DOCKET</div>
            <div className="col-span-3">MIX / DESCRIPTION</div>
            <div className="col-span-2 text-right">QTY (m³)</div>
            <div className="col-span-2 text-right">$/m³</div>
            <div className="col-span-2 text-right">AMOUNT</div>
            <div className="col-span-2 text-right">—</div>
          </div>
          {lines.map((l, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center py-1 border-b border-industrial/40" data-testid={`edit-line-${idx}`}>
              <input value={l.docket_no} onChange={(e) => patchLine(idx, { docket_no: e.target.value })} className="col-span-1 rounded-none bg-transparent border border-industrial px-1.5 py-1 mono text-xs" />
              <input value={l.mix_code} onChange={(e) => patchLine(idx, { mix_code: e.target.value })} className="col-span-3 rounded-none bg-transparent border border-industrial px-1.5 py-1 text-xs" />
              <input type="number" step="0.01" value={l.quantity_m3} onChange={(e) => patchLine(idx, { quantity_m3: e.target.value })} className="col-span-2 rounded-none bg-transparent border border-industrial px-1.5 py-1 text-xs mono text-right" />
              <input type="number" step="0.01" value={l.price_per_m3} onChange={(e) => patchLine(idx, { price_per_m3: e.target.value })} className="col-span-2 rounded-none bg-transparent border border-industrial px-1.5 py-1 text-xs mono text-right" />
              <input type="number" step="0.01" value={l.amount} onChange={(e) => patchLine(idx, { amount: e.target.value })} className="col-span-2 rounded-none bg-transparent border border-industrial px-1.5 py-1 text-xs mono text-right text-primary-orange font-bold" title="Auto-calculated from qty × rate. Override for line-only extras (credit adjustments etc.)" />
              <button onClick={() => removeLine(idx)} className="col-span-2 text-red-400 hover:text-red-300 text-xs uppercase text-right" data-testid={`remove-line-${idx}`}><Trash2 className="w-3.5 h-3.5 inline" /></button>
            </div>
          ))}
          <button type="button" onClick={addLine} className="mono text-[10px] uppercase tracking-widest text-primary-orange hover:underline mt-1" data-testid="add-line-btn">+ Add line</button>
        </div>

        {/* Totals preview */}
        <div className="border-t border-industrial pt-3 grid grid-cols-3 gap-3 text-xs mono">
          <div>
            <div className="label-tag">SUBTOTAL</div>
            <div className="text-ink text-lg mono">${subtotal.toFixed(2)}</div>
          </div>
          <div>
            <div className="label-tag">GST ({(taxRate * 100).toFixed(0)}%)</div>
            <div className="text-ink text-lg mono">${tax.toFixed(2)}</div>
          </div>
          <div>
            <div className="label-tag">NEW TOTAL</div>
            <div className={`text-2xl font-black mono ${total < 0 ? "text-emerald-400" : "text-primary-orange"}`} data-testid="edit-new-total">
              ${total.toFixed(2)}
              {isCredit && <span className="ml-2 text-[10px] font-normal text-emerald-400">CREDIT NOTE</span>}
            </div>
            {invoice.total !== undefined && (
              <div className="text-[10px] text-muted-industrial mt-0.5">Old total: ${invoice.total.toFixed(2)}</div>
            )}
          </div>
        </div>

        <div className="space-y-2 border-t border-industrial pt-3">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="edit-is-credit-label">
            <input type="checkbox" checked={isCredit} onChange={(e) => setIsCredit(e.target.checked)} data-testid="edit-is-credit-cb" />
            <span className="mono text-[10px] uppercase tracking-widest text-ink">Mark as CREDIT NOTE (adjustment, refund, correction)</span>
          </label>
          <div>
            <Label className="label-tag">Reason for edit (audit log)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Corrected rate for docket 4231, price was $280 not $310"
              data-testid="edit-reason-input"
              className="rounded-none bg-transparent border-industrial text-sm"
            />
          </div>
          <div>
            <Label className="label-tag">Notes (shown on PDF)</Label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              data-testid="edit-notes-input"
              className="w-full rounded-none bg-transparent border border-industrial p-2 text-sm mono"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="edit-resend-label">
            <input type="checkbox" checked={alsoResend} onChange={(e) => setAlsoResend(e.target.checked)} data-testid="edit-resend-cb" />
            <span className="mono text-[10px] uppercase tracking-widest text-ink">
              Also <b className="text-primary-orange">re-email</b> the updated invoice to the customer after saving
            </span>
          </label>
        </div>

        {invoice.edit_history?.length > 0 && (
          <div className="border-t border-industrial pt-3">
            <div className="label-tag mb-1">Previous edits ({invoice.edit_history.length})</div>
            <div className="max-h-24 overflow-y-auto space-y-1">
              {invoice.edit_history.map((h, idx) => (
                <div key={idx} className="mono text-[10px] text-muted-industrial">
                  {(h.at || "").slice(0, 16).replace("T", " ")} · {h.by} · ${h.old_total?.toFixed(2)} → ${h.new_total?.toFixed(2)} · {h.reason}
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="border-t border-industrial pt-3 sticky bottom-0 bg-surface">
          <Button onClick={onClose} variant="ghost" data-testid="edit-cancel-btn" className="rounded-none mono uppercase text-xs">Cancel</Button>
          <Button onClick={save} disabled={saving || reason.trim().length < 3} data-testid="edit-save-btn" className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold text-black">
            {saving ? "Saving…" : alsoResend ? "Save & Re-email" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
